import {
  ArrowUUpLeft,
  CaretDown,
  CaretLeft,
  Check,
  Checks,
  ImagesSquare,
  Copy,
  Info,
  Microphone,
  MagnifyingGlass,
  PaperPlaneTilt,
  Pause,
  PencilSimple,
  Plus,
  Play,
  PhoneX,
  Prohibit,
  X,
  VideoCameraSlash,
  Phone,
  Trash,
} from '@phosphor-icons/react';
import { DELAI_DE_MODIFICATION_MS, type Message, type PublicUser } from '@sirocco/shared';
import { FAMILLES_EMOJI } from '../../utils/emojis';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { Link, useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { events, messages, posts, users as usersApi } from '../../api/endpoints';
import { effacerLesNotificationsDe } from '../../native/notifications';
import { useSaisieExtensible } from '../../hooks/useSaisieExtensible';
import { ScreenError, ScreenLoader } from '../../components/layout/Screens';
import { Avatar, GroupAvatar } from '../../components/ui/Avatar';
import { BadgeEmoji } from '../../components/ui/Bits';
import { IconButton } from '../../components/ui/Button';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { PhotoViewer } from '../../components/ui/PhotoViewer';
import { VoiceApercu, VoicePlayer } from '../../components/ui/VoicePlayer';
import { Sheet } from '../../components/ui/Sheet';
import { seedScreenCache, useAsync } from '../../hooks/useAsync';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { placesDeLecture } from '../../hooks/useScrollMemory';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import {
  canRecordVoice,
  MAX_VOICE_SECONDS,
  startRecording,
  type VoiceRecording,
} from '../../utils/voice';
import { EventCard } from '../../components/EventCard';
import { PostCard as CartePost } from '../../components/PostCard';
import { useGlisserPourRepondre } from '../../hooks/useGlisserPourRepondre';
import type { PriseLongue } from '../../hooks/useLongPress';
import { useLongPress } from '../../hooks/useLongPress';
import { useCall } from '../../store/CallContext';
import { useSession } from '../../store/SessionContext';
import { dayLabel, timeOfDay } from '../../utils/format';
import { garderLeBrouillon, lireLeBrouillon } from '../../utils/brouillons';
import { sensX, t } from '../../i18n';

/**
 * L'onde vivante pendant l'enregistrement : nombre de barres visibles et
 * cadence de mesure. Quarante barres a 70 ms donnent un peu moins de
 * trois secondes d'historique — assez pour voir le rythme de la voix,
 * assez court pour que l'onde defile.
 */
/*
  En deca, on est « au bas » : le fil reste colle au dernier message et la
  pastille ne parait pas. Soixante pixels, soit moins d'une bulle — de
  quoi tolerer le rebond elastique de WebKit sans se croire plus haut
  qu'on ne l'est.
*/
const COLLE_PX = 60;

/**
 * Le temps que la mise en page se stabilise apres l'ouverture d'un fil.
 *
 * Personne n'ouvre le clavier dans la demi-seconde ou il entre dans une
 * conversation ; tout ce qui bouge pendant ce temps est la page qui se
 * pose.
 */
const CALAGE_MS = 500;

/*
  Le temps qu'on laisse a la descente animee d'un envoi.

  Assez pour couvrir le defilement et l'aller-retour du message avec la
  base ; au-dela, un message recu redevient ce qu'il doit etre — quelque
  chose qui ne deplace rien.
*/
const GLISSE_MS = 700;

/**
 * La sortie d'un message envoye : sa bulle grandit en montant, et le fil
 * monte avec elle. A tenir d'accord avec `ms-part` dans la feuille.
 */
const ENVOI_MS = 340;

const LIVE_BARS = 40;
const LIVE_STEP_MS = 70;

/**
 * En deca, l'appui compte comme bref : l'enregistrement continue seul.
 * Au-dela, c'est un appui maintenu et lacher termine.
 */
const TAP_MS = 350;

/**
 * L'icone d'appel video.
 *
 * Celle de Phosphor est trop carree : ses angles font un boitier, pas un
 * bouton, et elle jurait a cote du combine tout en courbes. Celle-ci
 * reprend la meme idee — un corps et son objectif — avec des aretes
 * franchement arrondies, comme sur les messageries qu'on connait.
 *
 * Un peu plus grande que le combine, et c'est voulu : son corps n'occupe
 * que la moitie de la hauteur du cadre la ou le combine le remplit, si
 * bien qu'a taille egale elle paraissait la plus petite des deux.
 */
function CameraArrondie({ size = 26 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2.2" y="6.2" width="13.6" height="11.6" rx="4.2" />
      <path d="M15.8 10.6l4.3-2.6a.9.9 0 0 1 1.4.78v6.44a.9.9 0 0 1-1.4.78l-4.3-2.6z" />
    </svg>
  );
}

/**
 * Une discussion.
 *
 * Cet ecran ne passe pas par `SubScreen` : son en-tete porte l'avatar de
 * l'interlocuteur, et son bas est occupe par le composeur, colle au
 * clavier. La mise en page lui est donc propre.
 */
/*
  Les emojis proposes, et il n'y en aura pas d'autres.

  Six tiennent sur une rangee sans serrer, meme sur un petit telephone,
  et couvrent ce a quoi un message appelle : l'accord, l'affection, le
  rire, la surprise, la peine, le merci. Ouvrir un clavier entier
  rendrait le geste plus long que la reponse qu'il remplace.
*/
/** Le repli du bandeau de reponse, a tenir d'accord avec la feuille. */
const SORTIE_REPONSE_MS = 200;
/** La descente de la palette, a tenir d'accord avec la feuille de style. */
const SORTIE_PALETTE_MS = 200;
/** L'effacement de la feuille du message, a tenir d'accord avec la feuille. */
const SORTIE_MENU_MS = 240;
/** Passe cette duree, on n'admire plus la descente : on l'attend. */
const DESCENTE_MAX_MS = 1000;
/** La pose d'un emoji, a tenir d'accord avec la feuille de style. */
const POSE_EMOJI_MS = 520;
/** Son retrait, plus bref : on regarde arriver, on ne regarde pas partir. */
const RETRAIT_EMOJI_MS = 260;

/**
 * La hauteur du rappel de reponse, mesuree au banc : 51 px, dont six
 * recouvrent le composeur. On reserve un peu plus, pour que le dernier
 * message respire au-dessus de lui.
 */
const HAUTEUR_RAPPEL_PX = 58;
/** Au-dela, le doigt a promene : c'est un defilement, pas un appui. */
const APPUI_PX = 8;
/**
 * Au-dela, le doigt s'est attarde : ce n'est plus un appui.
 *
 * Sous les 450 ms de l'appui long, sans quoi ouvrir la feuille d'un
 * message refermerait le clavier au relachement.
 */
const APPUI_MS = 300;

const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
/** Deux touches plus proches que cela, dans le temps et sur l'ecran, font un coeur. */
const DOUBLE_TOUCHE_MS = 320;
const DOUBLE_TOUCHE_PX = 24;
/** Le like du double appui. */
const COEUR = '❤️';

/*
  Les arrivees sur un message deja servies, par entree d'historique.

  Revenir sur le fil depuis ses infos remonte l'ecran avec la meme entree,
  donc le meme message vise : sans ce registre, on repartait sur lui au
  lieu de retrouver la lecture la ou on l'avait laissee.
*/
const arriveesServies = new Set<string>();

export function ConversationScreen() {
  const { conversationId = '' } = useParams();
  const { user } = useSession();
  const { appeler, occupe } = useCall();
  const navigate = useNavigate();
  /* Le retour au doigt, comme sur tous les sous-ecrans. */
  const swipe = useSwipeBack();

  const monId = user?.id ?? '';
  /* Le texte commence avant de sortir revient avec le fil. */
  const [draft, setDraft] = useState(() => lireLeBrouillon(monId, conversationId));
  /* L'heure du dernier envoi parti d'ici : ce qui est ecrit depuis sort du composeur. */
  const [partDepuis, setPartDepuis] = useState(0);
  /*
    Le message qu'on corrige. Le champ prend son texte le temps de la
    correction ; ce qu'on ecrivait avant attend a cote, et revient apres.
  */
  const [enModif, setEnModif] = useState<Message | null>(null);
  const brouillonGardeRef = useRef('');
  const brouillonDeRef = useRef(`${monId}.${conversationId}`);
  /* D'un fil a l'autre sans quitter l'ecran : chacun reprend le sien. */
  useEffect(() => {
    if (brouillonDeRef.current === `${monId}.${conversationId}`) return;
    brouillonDeRef.current = `${monId}.${conversationId}`;
    setEnModif(null);
    setDraft(lireLeBrouillon(monId, conversationId));
  }, [monId, conversationId]);
  /* Le message auquel on repond, choisi en le tirant vers la droite. */
  const [repondA, setRepondA] = useState<Message | null>(null);
  const [sending, setSending] = useState(false);
  /*
    Ce que le doigt long retient : le message, la place qu'il occupe a
    l'ecran, et son dessin tel quel.

    Le dessin est repris de la page (`outerHTML`) plutot que refabrique :
    une bulle de texte, une photo, un vocal et une carte d'evenement n'ont
    rien en commun, et les recopier une seconde fois aurait fait deux
    verites a tenir. Ce que la copie montre est, au pixel pres, ce qui
    etait la — c'est ce qui fait croire que le message s'est souleve.
  */
  const [held, setHeld] = useState<{ message: Message; rect: DOMRect; html: string } | null>(
    null,
  );
  const colonneRef = useRef<HTMLDivElement | null>(null);
  /* La palette entiere, ouverte par le « + » de la rangee. */
  const [palette, setPalette] = useState(false);
  const [quete, setQuete] = useState('');
  /* La palette est-elle depliee. La recherche l'ouvre, le doigt aussi. */
  const [chercheEmoji, setChercheEmoji] = useState(false);
  /* Elle s'en va en glissant : on la garde le temps de sa descente. */
  const [palettePart, setPalettePart] = useState(false);
  const sortiePaletteRef = useRef<number | null>(null);
  const paletteRef = useRef<HTMLDivElement | null>(null);
  const saisieEmojiRef = useRef<HTMLInputElement | null>(null);
  const priseRef = useRef<{ y: number; depart: number } | null>(null);
  const imagePriseRef = useRef<number | null>(null);
  const viseeRef = useRef(0);

  /*
    Les deux hauteurs entre lesquelles la palette va et vient.

    La basse laisse voir le message souleve et ce qui l'entoure ; la haute
    prend tout ce qui reste sous l'en-tete, clavier deduit. Elles sont
    calculees et non ecrites dans la feuille, parce que le doigt a besoin
    de nombres pour s'y arreter.
  */
  function hauteursDeLaPalette() {
    const lift =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--keyboard-lift')) ||
      0;
    return { basse: window.innerHeight * 0.52, haute: window.innerHeight - 132 - lift };
  }

  /*
    La tirer vers le haut, la rendre au bas.

    On la depliait en touchant la recherche, ce qui obligeait a ouvrir un
    clavier pour voir plus d'emojis. Le doigt suffit : elle suit, s'arrete
    ou on la lache, puis rejoint la hauteur la plus proche.

    La hauteur est ecrite directement, une fois par image : passer par un
    etat rejouerait tout le catalogue a chaque pixel.
  */
  /**
   * Refermer la palette en la laissant redescendre.
   *
   * Elle sortait du document sur-le-champ : elle montait a l'ouverture et
   * disparaissait a la fermeture, ce qui n'allait pas ensemble. Elle
   * glisse maintenant dans les deux sens.
   */
  function fermerLaPalette() {
    if (sortiePaletteRef.current !== null) window.clearTimeout(sortiePaletteRef.current);
    setPalettePart(true);
    setQuete('');
    setChercheEmoji(false);
    saisieEmojiRef.current?.blur();
    sortiePaletteRef.current = window.setTimeout(() => {
      sortiePaletteRef.current = null;
      setPalettePart(false);
      setPalette(false);
    }, SORTIE_PALETTE_MS);
  }

  useEffect(
    () => () => {
      if (sortiePaletteRef.current !== null) window.clearTimeout(sortiePaletteRef.current);
    },
    [],
  );

  const prisePalette = {
    onPointerDown(evenement: React.PointerEvent<HTMLElement>) {
      const palette = paletteRef.current;
      if (!palette) return;
      evenement.currentTarget.setPointerCapture?.(evenement.pointerId);
      priseRef.current = { y: evenement.clientY, depart: palette.getBoundingClientRect().height };
      palette.style.transition = 'none';
    },
    onPointerMove(evenement: React.PointerEvent<HTMLElement>) {
      const prise = priseRef.current;
      const palette = paletteRef.current;
      if (!prise || !palette) return;
      const { basse, haute } = hauteursDeLaPalette();
      viseeRef.current = Math.max(basse, Math.min(haute, prise.depart + (prise.y - evenement.clientY)));
      if (imagePriseRef.current === null) {
        imagePriseRef.current = requestAnimationFrame(() => {
          imagePriseRef.current = null;
          palette.style.height = `${viseeRef.current}px`;
        });
      }
    },
    onPointerUp() {
      const prise = priseRef.current;
      const palette = paletteRef.current;
      priseRef.current = null;
      if (!prise || !palette) return;
      if (imagePriseRef.current !== null) {
        cancelAnimationFrame(imagePriseRef.current);
        imagePriseRef.current = null;
      }
      const { basse, haute } = hauteursDeLaPalette();
      const ou = viseeRef.current || prise.depart;
      /* La plus proche des deux : on ne laisse pas la palette entre
         deux, elle est ouverte ou elle ne l'est pas. */
      const depliee = ou - basse > haute - ou;
      palette.style.transition = '';
      palette.style.height = '';
      setChercheEmoji(depliee);
      if (!depliee) saisieEmojiRef.current?.blur();
    },
  };

  /*
    Ce que la recherche retient.

    Champ vide, on rend les familles telles quelles — parcourir reste le
    geste par defaut. Sinon on garde, dans chacune, les signes dont les
    mots contiennent ce qui est tape, et l'on jette les familles restees
    vides : une rubrique sans rien dedans n'apprend rien.
  */
  const trouves = useMemo(() => {
    const mot = quete.trim().toLowerCase();
    if (!mot) return FAMILLES_EMOJI;
    return FAMILLES_EMOJI.map((famille) => ({
      ...famille,
      signes: famille.signes.filter(([, mots]) => mots.includes(mot)),
    })).filter((famille) => famille.signes.length > 0);
  }, [quete]);

  /*
    Les emojis poses sur les messages du fil.

    Ecoute a part du fil lui-meme : une reaction n'appartient pas a
    l'auteur du message mais a celui qui la pose, elle est donc rangee
    ailleurs — et elle va et vient sans que le message bouge.
  */
  /*
    Les emojis viennent d'abord du chargement, ensuite de l'ecoute.

    L'etat ne porte que ce qui a bouge depuis : tant qu'il est nul, ce
    sont ceux arrives avec le fil qui s'affichent — donc des le premier
    rendu, sans pastille qui se glisse apres coup sous une bulle en la
    grandissant.
  */
  const [reactionsVives, setReactionsVives] = useState<Record<
    string,
    Record<string, string>
  > | null>(null);

  /* Le champ de saisie, pour lui rendre le clavier des qu'on choisit un
     message a citer. */
  const saisieRef = useRef<HTMLTextAreaElement | null>(null);

  /*
    Le bandeau s'en va, il ne disparait pas.

    React le demonterait sur-le-champ ; on le garde le temps qu'il se
    replie dans la saisie, comme il en etait sorti. Le minuteur est
    retenu pour qu'un nouveau message choisi entre-temps ne se fasse pas
    effacer par la fin de l'animation precedente.
  */
  const [repondPart, setRepondPart] = useState(false);
  const sortieRef = useRef<number | null>(null);
  /* Ecrivait-on quand la feuille du message s'est ouverte. */
  const ecrivaitRef = useRef(false);
  /* Le message d'origine, efface le temps que sa copie soit soulevee. */
  const origineRef = useRef<HTMLElement | null>(null);

  /*
    Le clavier ne se leve que si on l'a demande.

    Trois temoins ont ete essayes pour savoir s'il fallait le rendre ou
    non — la reserve du bas, les annonces du systeme, le focus lui-meme —
    et chacun s'est trompe un jour. On ne cherche donc plus a savoir : on
    refuse.

    La saisie n'accepte le focus que precede d'un des gestes qui y
    donnent droit : un doigt pose sur elle, ou un message tire pour y
    repondre. Tout autre focus — d'ou qu'il vienne, connu ou non — est
    rendu aussitot. La regle ne depend plus de rien qu'il faille tenir a
    jour, et elle couvre les causes qu'on n'a pas identifiees.
  */
  const autoriseRef = useRef(false);

  /*
    Un clavier parti doit laisser la saisie inoccupee.

    Le mouchard a tranche : quand le clavier se relevait tout seul, aucun
    focus nouveau n'arrivait — c'est que la saisie ne l'avait jamais
    perdu. Un clavier baisse au doigt, ou renvoye par le systeme, laisse
    le champ premier repondant ; WebKit represente alors son clavier a la
    premiere occasion, et faire defiler le fil en est une.

    On coupe donc a la source : des que le clavier s'en va, la saisie est
    liberee. Plus personne ne l'attend, donc plus rien a represente.
  */
  useEffect(() => {
    let vivant = true;
    const arrets: Array<{ remove: () => Promise<void> }> = [];
    const liberer = () => {
      if (document.activeElement === saisieRef.current) saisieRef.current?.blur();
    };
    void Keyboard.addListener('keyboardWillHide', liberer).then((a) =>
      vivant ? arrets.push(a) : void a.remove(),
    );
    void Keyboard.addListener('keyboardDidHide', liberer).then((a) =>
      vivant ? arrets.push(a) : void a.remove(),
    );
    return () => {
      vivant = false;
      for (const a of arrets) void a.remove();
    };
  }, []);

  /*
    Mouchard temporaire : qui prend le focus.

    La saisie refuse desormais tout clavier qu'on ne lui a pas demande, et
    il se leve encore : il ne passe donc pas par elle. Plutot que de
    deviner une quatrieme fois, on ecrit a l'ecran le nom de ce qui recoit
    le focus, avec l'endroit d'ou l'on venait. A retirer des que la
    reponse est connue.
  */

  function autoriserLeClavier() {
    autoriseRef.current = true;
    /* L'autorisation ne vaut que pour l'instant qui suit : une porte
       laissee ouverte serait la meme faille sous un autre nom. */
    window.setTimeout(() => {
      autoriseRef.current = false;
    }, 400);
  }

  /* La feuille s'efface avant de partir : le clavier qui revient au meme
     instant paraissait sinon surgir. */
  /* Le message dont on regarde les reactions, s'il y en a un. */
  const [reacteurs, setReacteurs] = useState<Message | null>(null);
  const [menuPart, setMenuPart] = useState(false);
  const sortieMenuRef = useRef<number | null>(null);

  /**
   * Refermer la feuille, et rendre le clavier a qui ecrivait.
   *
   * Le focus part dans la foulee du clic — seul moment ou le systeme
   * accorde un clavier — tandis que la feuille, elle, prend le temps de
   * s'effacer.
   */
  /**
   * Refermer la feuille sur-le-champ, en rendant le message.
   *
   * Le soulevement efface l'original ; il faut donc le rendre, et par
   * tous les chemins qui referment. Repondre, Infos et Supprimer
   * passaient a cote : le message restait invisible, et rien ne le
   * ramenait avant un rechargement du fil.
   */
  function fermerLaFeuilleDUnCoup() {
    if (sortieMenuRef.current !== null) {
      window.clearTimeout(sortieMenuRef.current);
      sortieMenuRef.current = null;
    }
    setMenuPart(false);
    setPalette(false);
    setQuete('');
    setChercheEmoji(false);
    setHeld(null);
    if (origineRef.current) origineRef.current.style.visibility = '';
    origineRef.current = null;
  }

  function refermerLaFeuille() {
    if (ecrivaitRef.current) {
      /* On rend ce qu'on a pris : la feuille avait ecarte la saisie. */
      autoriserLeClavier();
      saisieRef.current?.focus();
    }
    if (sortieMenuRef.current !== null) window.clearTimeout(sortieMenuRef.current);
    setMenuPart(true);
    sortieMenuRef.current = window.setTimeout(() => {
      sortieMenuRef.current = null;
      setMenuPart(false);
      setPalette(false);
      setHeld(null);
      /* La copie a rejoint sa place : l'original peut la reprendre. */
      if (origineRef.current) origineRef.current.style.visibility = '';
      origineRef.current = null;
    }, SORTIE_MENU_MS);
  }

  useEffect(
    () => () => {
      if (sortieMenuRef.current !== null) window.clearTimeout(sortieMenuRef.current);
      /* Quitter l'ecran pendant qu'un message est souleve ne doit pas le
         laisser invisible au retour. */
      if (origineRef.current) origineRef.current.style.visibility = '';
    },
    [],
  );

  function annulerLaReponse() {
    if (sortieRef.current !== null) window.clearTimeout(sortieRef.current);
    setRepondPart(true);
    /*
      La croix est dans le composeur : la toucher fait perdre le focus a
      la saisie, donc on le lui rend. On ne rend que ce qu'on prend, et
      seulement si l'on ecrivait — sans clavier, elle ne fait qu'annuler.
    */
    if (document.activeElement === saisieRef.current) {
      autoriserLeClavier();
      saisieRef.current?.focus();
    }
    sortieRef.current = window.setTimeout(() => {
      sortieRef.current = null;
      setRepondPart(false);
      setRepondA(null);
    }, SORTIE_REPONSE_MS);
  }

  useEffect(
    () => () => {
      if (sortieRef.current !== null) window.clearTimeout(sortieRef.current);
    },
    [],
  );

  /* Ce qui est affiche, garde a portee de l'ecoute : elle doit pouvoir
     reconnaitre qu'elle ne dit rien de neuf. */
  const affichees = useRef<string>('');

  useEffect(() => {
    if (!conversationId) return;
    /* On change de conversation : ce qu'on ecoutait ne la concerne plus. */
    setReactionsVives(null);
    return messages.watchReactions(conversationId, (recues) => {
      /*
        L'ecoute repond aussitot, et redit d'abord exactement ce que le
        chargement vient de donner. Reposer cette valeur refaisait un
        rendu entier du fil pour rien — un de ces petits soubresauts
        qu'on voit en ouvrant une conversation. On ne repose que ce qui a
        change.
      */
      const signature = JSON.stringify(recues);
      if (signature === affichees.current) return;
      affichees.current = signature;
      setReactionsVives(recues);
    });
  }, [conversationId]);

  /*
    Les emojis arrivent apres le fil, et les messages grandissent.

    C'est le petit sursaut qu'on voyait en ouvrant une conversation : le
    fil se posait sur son dernier message, puis les pastilles se
    glissaient sous les bulles qui en portaient. Le navigateur garde le
    defilement ou il est, donc tout ce qui a grandi pousse le contenu
    vers le bas — et de la ou l'on regarde, la conversation a remonte.

    On ne recolle qu'a une condition : etre colle au dernier message.
    Plus haut dans l'historique, c'est l'utilisateur qui decide ou il
    regarde, et une pastille qui arrive ne doit rien deplacer.

    L'ecart au bas, et non `enBas`. Cet etat vaut `true` au montage et
    n'est corrige qu'apres coup : a l'ouverture d'une conversation
    quittee en cours de lecture, il affirmait encore qu'on etait en bas,
    et le recollage ecrasait la position tout juste restauree. L'ecart
    est une reference, donc il dit la verite au moment ou on la lit —
    c'est deja lui qui tient tout le reste de cet ecran.
  */

  /*
    La copie doit tomber exactement ou etait le message.

    Mesure plutot que calcul : la rangee d'emojis et la liste d'actions
    n'ont pas de hauteur fixe — le nombre d'actions depend de qui a
    ecrit. On pose donc la colonne apres l'avoir dessinee, decalee de la
    hauteur de ce qui precede la copie, puis on la ramene dans l'ecran si
    elle en deborde. Un message tout en bas remonte alors juste ce qu'il
    faut, et c'est plus honnete que de le laisser sous les actions.
  */
  /*
    Le fil ne bouge pas pendant qu'un message est souleve.

    Chercher un emoji leve un clavier, et la reserve que la page se fait
    en bas remontait alors toute la conversation — visible a travers le
    flou, derriere le message. Elle n'a aucune raison de bouger : rien
    n'est a degager, la saisie est cachee et le fil ne se lit plus. On la
    suspend le temps de la feuille, et le clavier ne pousse plus que la
    palette, qui l'a demande.
  */
  useEffect(() => {
    document.documentElement.classList.toggle('fil-fige', held !== null);
    return () => document.documentElement.classList.remove('fil-fige');
  }, [held]);

  useLayoutEffect(() => {
    const colonne = colonneRef.current;
    if (!held || !colonne) return;
    const copie = colonne.querySelector('.ms-menu__copie') as HTMLElement | null;
    if (!copie) return;

    const MARGE = 14;
    const haut = colonne.getBoundingClientRect().height;
    const avant = copie.offsetTop;
    /* La palette mange le bas de l'ecran : le message remonte devant
       elle plutot que de passer dessous. */
    /* La palette mange le bas de l'ecran ; le message souleve remonte
       devant elle plutot que de passer dessous. Chercher ne le deplace
       pas davantage : la palette monte devant lui, elle ne le pousse
       pas. */
    const disponible = window.innerHeight * (palette ? 0.45 : 1);
    const voulu = held.rect.top - avant;
    const place = Math.min(Math.max(voulu, MARGE), disponible - haut - MARGE);
    const pose = Math.max(MARGE, place);
    /*
      La premiere pose ne s'anime pas, les suivantes si.

      A l'ouverture il n'y a pas d'ou venir : le message se pose la ou il
      etait. Ensuite chaque changement de place — la palette qui s'ouvre,
      celle qu'on referme — est un trajet, et il se regarde. On coupe donc
      la transition le temps de la premiere valeur, puis on la rend.
    */
    const premiere = colonne.style.top === '';
    if (premiere) colonne.style.transition = 'none';
    colonne.style.top = `${pose}px`;
    if (premiere) {
      void colonne.offsetHeight;
      colonne.style.transition = '';
    }

    /*
      De combien la copie doit remonter pour rejoindre sa place.

      Elle commence exactement la ou etait le message — meme place, meme
      pixel — puis s'eleve jusqu'a la ou la colonne tient dans l'ecran.
      C'est ce trajet qui fait qu'on souleve un message au lieu d'en
      faire apparaitre un autre. Quand rien ne deborde, il vaut zero et
      la copie ne bouge pas.
    */
    colonne.style.setProperty('--souleve', `${voulu - pose}px`);
  }, [held, palette]);
  /*
    La feuille « Infos » d'un de mes messages : quand il a ete remis, et
    quand l'autre l'a vu. Les reperes se lisent a l'ouverture — ils
    peuvent avoir bouge depuis le chargement du fil.
  */
  const [infos, setInfos] = useState<{
    message: Message;
    reperes: Record<string, number> | null;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /* Ce que la pastille de retour au bas doit dire, s'il faut la montrer. */
  const [enBas, setEnBas] = useState(true);
  const [nouveaux, setNouveaux] = useState(0);
  /* La photo ouverte, et la place qu'occupait sa vignette : c'est de la
     que part l'agrandissement. */
  const [viewed, setViewed] = useState<{ src: string; from: DOMRect } | null>(null);
  /* Le viseur de l'application, ouvert par le bouton appareil photo. */
  const { pending, request, cancel, confirm } = useConfirm();
  const scrollRef = useRef<HTMLDivElement>(null);
  /*
    Le geste du fil principal, aux deux bouts de la conversation : tirer
    au bord l'etire, une liste lancee qui touche le bord rebondit. Ni
    roue ni relecture — les messages arrivent seuls. Le rebond ne suit
    que l'elan d'un doigt : le fil se recale aussi tout seul en bas.
  */
  const contenuRef = useRef<HTMLDivElement>(null);
  const geste = { elanDuDoigt: true, zoneFixe: true } as const;
  usePullToRefresh(scrollRef, contenuRef, undefined, undefined, { ...geste, bord: 'haut' });
  usePullToRefresh(scrollRef, contenuRef, undefined, undefined, { ...geste, bord: 'bas' });
  /* Ou et quand le doigt s'est pose sur le fil : c'est ce qui distingue
     un appui d'un defilement. */
  const contactRef = useRef<{ x: number; y: number; a: number } | null>(null);
  /* La descente vers le dernier message, quand elle est en cours. */
  const descenteRef = useRef<number | null>(null);
  /* Cette descente-la est la montee d'un envoi : elle suit le bas, et le
     releve de la place de lecture l'attend. */
  const monteeRef = useRef(false);
  const glisseMinuteurRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (descenteRef.current !== null) cancelAnimationFrame(descenteRef.current);
    },
    [],
  );
  const composerRef = useRef<HTMLFormElement>(null);

  /*
    Le fil et sa fiche arrivent ensemble : l'en-tete ne doit pas clignoter.

    Une cle de cache, une par discussion. Sans elle, tout remontage de
    l'ecran repartait de rien : trois points sur un fil vide, puis le
    contenu qui revient. C'est ce qu'on voyait en abandonnant un retour au
    doigt en chemin — l'ecran se remonte pour une raison ou une autre, et
    il n'y a aucune raison qu'il en profite pour se vider. Avec elle, le
    fil connu s'affiche au premier rendu et le rafraichissement se fait
    derriere, sans que rien ne clignote.
  */
  const { data, loading, error, reload, setData } = useAsync(
    () =>
      Promise.all([
        messages.conversation(conversationId),
        messages.thread(conversationId),
        /* Les emojis partent avec le fil, pas apres lui : sinon les
           pastilles se glissent sous les bulles une fraction de seconde
           plus tard, en les grandissant. Un fil qui s'ouvre doit etre
           fini quand il s'affiche. */
        messages.reactions(conversationId),
      ])
        .then(async ([conversation, thread, reactions]) => {
          await prechargerLesCartes(thread);
          return { conversation, thread, reactions };
        }),
    [conversationId],
    `conversation:${conversationId}`,
  );

  const reactions = useMemo(() => reactionsVives ?? data?.reactions ?? {}, [reactionsVives, data]);

  /* Ce que l'ecoute doit reconnaitre comme deja vu. Pose ici plutot qu'au
     chargement : c'est le seul endroit qui sait ce qui est reellement a
     l'ecran. */
  affichees.current = useMemo(() => JSON.stringify(reactions), [reactions]);

  /* Les cartes de post et d'evenement se remplissent apres le fil : leur
     arrivee est un changement de hauteur de plus a rattraper. */
  const [cartes, setCartes] = useState(0);
  useEffect(() => surCartePrete(() => setCartes((n) => n + 1)), []);

  useLayoutEffect(() => {
    const view = scrollRef.current;
    if (!view || gapRef.current > COLLE_PX) return;
    /* Avant la peinture, pas apres : pose dans un effet ordinaire, la
       correction arrivait une image trop tard et le sursaut restait
       visible — plus court, mais visible. */
    view.scrollTop = view.scrollHeight - view.clientHeight;
    /* `repondA` et `repondPart` : la reserve du bas change avec eux, donc
       le bas du fil aussi. */
  }, [reactions, cartes, repondA, repondPart]);

  /*
    Arriver sur un message trouve par la recherche des discussions.

    Deux images apres le fil : la premiere mesure de la vue y repose la
    lecture d'avant, et le saut doit passer apres elle, pas sous elle.
  */
  const emplacement = useLocation();
  const vise = (emplacement.state as { message?: string } | null)?.message ?? null;
  useEffect(() => {
    if (!vise || !data || arriveesServies.has(emplacement.key)) return;
    let image = requestAnimationFrame(() => {
      image = requestAnimationFrame(() => {
        arriveesServies.add(emplacement.key);
        allerAuMessage(vise, 'auto');
      });
    });
    return () => cancelAnimationFrame(image);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vise, data, emplacement.key]);

  /*
    Ce qui arrive pendant qu'on lit.

    La base previent des qu'un message est ecrit : il s'ajoute au fil
    sans que l'ecran bouge et sans rien relire. C'est la reponse a « il
    faut sortir et revenir pour voir ce qu'on m'a ecrit », et ce n'est
    que le message lui-meme qui transite, pas tout l'historique.
  */
  /*
    La borne est prise une fois, au premier chargement : sans le garde
    ci-dessous, chaque message ajoute changerait la borne, donc
    rebrancherait l'ecoute — a l'infini.
  */
  /*
    « Il écrit… ».

    L'ecoute ne vit que dans le fil ouvert, et la marque s'efface d'
    elle-meme : c'est ce qui la garde bornee.
  */
  const [frappent, setFrappent] = useState<string[]>([]);

  useEffect(() => {
    if (!conversationId) return;
    const arret = messages.watchFrappe(conversationId, (qui) =>
      /*
        Meme precaution qu'au-dessus : l'ecoute part une premiere fois des
        qu'on la branche, en general avec personne. Reposer une liste vide
        sur une liste vide relancerait un rendu pour rien.
      */
      setFrappent((precedent) =>
        precedent.length === qui.length && precedent.every((uid, rang) => uid === qui[rang])
          ? precedent
          : qui,
      ),
    );
    return () => {
      arret();
      // Quitter le fil, c'est arreter d'y ecrire.
      messages.arreterFrappe(conversationId);
    };
  }, [conversationId]);

  const fil = useRef<Message[]>([]);
  fil.current = data?.thread ?? [];
  const charge = Boolean(data);

  useEffect(() => {
    if (!conversationId || !charge) return;
    return messages.watchThread(conversationId, fil.current.at(-1) ?? null, (maj) =>
      setData((precedent) => {
        const thread = maj(precedent.thread);
        /*
          Rien n'a bouge : on rend l'objet precedent tel quel. En
          fabriquer un neuf ferait repartir l'effet de defilement, qui
          recollerait le fil au dernier message — c'est ce petit saut
          qu'on voyait en ouvrant une conversation.
        */
        return thread === precedent.thread ? precedent : { ...precedent, thread };
      }),
    );
  }, [conversationId, charge, setData]);

  /*
    Une discussion s'ouvre sur son dernier message — sauf si on l'avait
    quittee ailleurs.

    Elle se recollait au bas a chaque arrivee de donnees, ce qui se voyait
    comme une reinitialisation des que l'ecran se remontait : un retour au
    doigt abandonne en chemin renvoyait au message le plus recent, meme si
    l'on etait en train de lire plus haut. C'est l'ecart au bas qui est
    repose, et il vaut zero tant qu'on n'a rien fait defiler — donc le
    dernier message, comme avant, la premiere fois.
  */
  useEffect(() => {
    const view = scrollRef.current;
    if (!view) return;

    /*
      Quelqu'un a ecrit depuis qu'on est parti : on descend au dernier
      message plutot que de reprendre a l'endroit quitte. Sinon on garde
      sa place — c'est tout l'interet de l'avoir retenue.

      La comparaison se refait a chaque arrivee de fil, et non une seule
      fois : le fil arrive deux fois, d'abord la version gardee en memoire
      — celle d'avant, sans le message neuf — puis la fraiche, quelques
      dixiemes plus tard. S'arreter a la premiere revenait a conclure
      qu'il n'y avait rien de neuf, et a rouvrir la conversation en
      ignorant justement le message qu'on venait lire.

      Elle s'arrete des que le doigt se pose sur le fil : a partir de la,
      c'est l'utilisateur qui decide ou il regarde, et un message qui
      arrive ne doit plus rien deplacer.
    */
    const brut = data?.thread.at(-1)?.createdAt;
    /* Une date ISO, ramenee en millisecondes : c'est ce qui se compare. */
    const dernier = brut ? Date.parse(brut) : 0;
    let descendre = false;

    if (!doigtRef.current && dernier > vuRef.current) {
      vuRef.current = dernier;
      gapRef.current = 0;
      descendre = true;
    }

    /*
      Deja au bas : on y reste, et le fil s'allonge sous les yeux. C'est
      le seul cas ou un message qui arrive deplace l'ecran — et il ne le
      deplace pas vraiment, il le maintient colle au plus recent.
    */
    if (gapRef.current <= COLLE_PX) {
      gapRef.current = 0;
      vuRef.current = dernier;
      descendre = true;
    }

    /*
      Remonte dans le fil : on ne touche a rien du tout.

      On reposait l'ecart au bas a chaque arrivee, ce qui remontait le
      contenu de la hauteur du message recu — quelques dizaines de pixels
      a chaque fois, et la lecture sautait a chaque message d'une salve.
      L'ecart avait un sens quand la vue changeait de taille sous le
      clavier ; il n'en a aucun quand c'est le contenu qui s'allonge par
      le bas. Ne rien faire garde la position exacte : le navigateur s'en
      charge tout seul.
    */
    if (descendre) {
      if (glisseRef.current) monterAuBas();
      else view.scrollTo({ top: view.scrollHeight - view.clientHeight, behavior: 'auto' });
    }
    annoncer();
  }, [data]);

  /*
    Le fil glisse avec le clavier, il ne saute pas.

    Ce qu'on avait juste au-dessus du composeur doit y rester : c'est la
    distance au bas du fil qu'on conserve, jamais la position absolue.
    Deja au dernier message, on y reste ; au milieu de l'historique, on
    garde sa page. Sauter au dernier message a chaque fois arrachait la
    lecture a qui remontait une conversation.

    Rien ici ne connait le clavier : on observe la zone changer de taille,
    c'est tout. Le bas de la vue web est accroche au clavier cote natif, si
    bien qu'une montee, une descente ou un renvoi au doigt se presentent
    tous de la meme facon — une hauteur qui change, image apres image.
  */
  const gapRef = useRef(placesDeLecture.get(conversationId)?.ecart ?? 0);
  /** Horodatage de la derniere retaille, pour ne pas relever pendant. */
  const resizedRef = useRef(0);
  /** Quand la vue a ete montee, et si la lecture y a deja ete reposee. */
  const monteRef = useRef(0);
  const poseeRef = useRef(false);
  /** La position absolue quittee, s'il y en a une a reprendre. */
  const hautRef = useRef(placesDeLecture.get(conversationId)?.haut ?? null);
  /*
    Une descente animee est en cours.

    Elle ne dure que le temps de l'envoi, et pendant ce temps le fil
    s'allonge — le message part, revient de la base, et le bas se deplace
    sous l'animation. Le drapeau dit a la regle de reviser sa cible en
    douceur plutot que d'y sauter, ce qui couperait le mouvement en deux.
  */
  const glisseRef = useRef(false);

  /** La date du dernier message connu en arrivant : le point de comparaison. */
  const vuRef = useRef(placesDeLecture.get(conversationId)?.dernier ?? 0);
  /*
    Le doigt s'est pose sur le fil.

    Tant qu'il ne l'a pas fait, l'ecran est encore en train de s'ouvrir et
    peut descendre au message neuf. Apres, c'est l'utilisateur qui decide
    ou il regarde : un message qui arrive pendant qu'il lit ne doit plus
    rien deplacer — ce cas-la est tenu par l'ecart, constant quand le fil
    s'allonge.
  */
  const doigtRef = useRef(false);

  useEffect(() => {
    const view = scrollRef.current;
    if (!view) return;
    const prendreLaMain = () => {
      doigtRef.current = true;
    };
    view.addEventListener('touchstart', prendreLaMain, { passive: true });
    return () => view.removeEventListener('touchstart', prendreLaMain);
  }, []);

  useEffect(() => {
    const view = scrollRef.current;
    if (!view) return;
    monteRef.current = performance.now();

    const observer = new ResizeObserver(() => {
      /*
        La premiere mesure repose la lecture la ou on l'avait laissee :
        c'est elle, et elle seule, qui restaure la position a l'ouverture.
      */
      const premiere = !poseeRef.current;
      poseeRef.current = true;

      /*
        On reprend la position **telle qu'on l'avait laissee**, et non la
        distance au bas : un message arrive pendant l'absence allonge le
        fil par le bas, et garder la distance au bas remontait le contenu
        de la hauteur de ce message. Ce qu'on lisait doit rester ou on
        l'avait laisse.

        Sauf si on etait colle au dernier message : la, le bas **est** la
        position, et il faut suivre ce qui s'est ajoute. C'est le cas que
        la ligne suivante traite deja, et il fonctionnait.
      */
      if (premiere && hautRef.current !== null && gapRef.current > COLLE_PX) {
        resizedRef.current = performance.now();
        /* Bornee elle aussi : la position gardee vient d'une vue qui
           n'avait pas forcement cette taille. */
        view.scrollTop = Math.min(
          Math.max(0, view.scrollHeight - view.clientHeight),
          hautRef.current,
        );
        return;
      }

      /*
        Les suivantes, dans la foulee du montage, ne sont pas le clavier :
        c'est la mise en page qui se stabilise — une barre qui se mesure,
        une marge d'encoche qui se resout. Reposer l'ecart au bas a ce
        moment-la deplacait le fil de quelques pixels juste apres son
        ouverture. Colle au dernier message, cela ne se voyait pas : le
        bas reste le bas. Remonte dans l'historique, cela sautait aux
        yeux — c'est exactement ce qui etait signale.

        Passe ce delai, une vue qui change de taille, c'est le clavier, et
        la le fil doit bien suivre.
      */
      if (!premiere && performance.now() - monteRef.current < CALAGE_MS) return;

      resizedRef.current = performance.now();
      /* Bornee : une vue qui grandit fait baisser le maximum, et rien ne
         garantit que l'ecart retenu tienne encore dedans. */
      const bas = Math.max(0, view.scrollHeight - view.clientHeight);
      view.scrollTop = Math.min(bas, Math.max(0, bas - gapRef.current));
    });

    observer.observe(view);
    return () => observer.disconnect();
  }, []);

  /*
    Un media n'a pas de hauteur avant d'etre charge.

    Une image dont on ne connait pas les dimensions ne mesure rien tant
    qu'elle n'est pas arrivee — `height: auto` vaut zero — et la
    couverture d'une video la fait passer de cent cinquante a trois cent
    vingt pixels, mesure. On se cale donc au bas d'un fil plus court qu'il
    ne sera, et le media, en prenant sa place, nous laisse d'autant plus
    haut : c'est ce qui ouvrait la conversation un peu au-dessus du
    dernier message. Seulement celles dont les derniers messages portent
    une photo ou une video, et seulement tant qu'elle n'est pas en cache
    — d'ou l'impression que cela n'arrive que parfois.

    Aucun evenement ne l'annonce. `load` couvrirait l'image, mais rien
    n'est emis quand la couverture d'une video s'affiche — verifie. On
    observe donc les medias eux-memes, les seuls dont la hauteur change
    apres coup, et ils se comptent sur les doigts d'une main.

    On ne recolle que si l'on etait deja au bas — la meme regle que pour
    un message qui arrive : remonte dans l'historique, rien ne doit
    deplacer la lecture.
  */
  useEffect(() => {
    const view = scrollRef.current;
    if (!view) return;

    const observer = new ResizeObserver(() => {
      if (gapRef.current > COLLE_PX) return;
      // Ne pas couper la montee d'un envoi : elle relit le bas a chaque image.
      if (monteeRef.current) return;
      view.scrollTo({ top: view.scrollHeight - view.clientHeight, behavior: 'auto' });
    });

    view.querySelectorAll('.ms-photo, .ms-video').forEach((media) => observer.observe(media));
    return () => observer.disconnect();
  }, [data]);

  /*
    La place de lecture se releve au fil du defilement — c'est le seul
    moment ou elle veut dire quelque chose. Pas pendant une retaille : le
    defilement y est provoque par le recollage lui-meme, qui se relirait
    alors et deriverait.
  */
  /**
   * Ce que la pastille montre : elle parait des qu'on s'eloigne du bas, et
   * porte le nombre de messages recus depuis qu'on s'en est eloigne.
   *
   * Les siens n'y comptent pas : on sait ce qu'on vient d'ecrire.
   */
  function annoncer() {
    const loin = gapRef.current > COLLE_PX;
    setEnBas(!loin);
    setNouveaux(
      loin
        ? (data?.thread ?? []).filter(
            (message) =>
              message.senderId !== user?.id && Date.parse(message.createdAt) > vuRef.current,
          ).length
        : 0,
    );
  }

  /**
   * Envoyer, c'est revenir au bas.
   *
   * Ce que j'ecris, je veux le voir partir. Rester au milieu de
   * l'historique apres avoir envoye donne l'impression que le message
   * n'est pas passe — et l'on redescend a la main pour verifier.
   *
   * L'ecart est mis a zero autant que l'ecran est descendu : c'est lui
   * que la regle relit a chaque arrivee de fil, et c'est ce qui garde le
   * bas quand le message envoye revient de la base une seconde plus tard.
   */
  function suivreSonEnvoi() {
    gapRef.current = 0;
    setEnBas(true);
    setNouveaux(0);
    monterAuBas();
  }

  /*
    A poser juste avant l'envoi, et non apres : le SDK range le message
    dans le fil des l'appel, sans attendre la base. Poser le drapeau
    ensuite, c'etait laisser la bulle tomber d'un coup et le fil sauter au
    bas, avant toute animation — l'envoi « sec ».

    Le drapeau tient le temps de la montee et de l'aller-retour avec la
    base : sans lui, le message qui revient sauterait au bas et couperait
    le mouvement en deux.
  */
  function preparerLEnvoi() {
    setPartDepuis(Date.now());
    gapRef.current = 0;
    setEnBas(true);
    setNouveaux(0);
    glisseRef.current = true;
    if (glisseMinuteurRef.current !== null) window.clearTimeout(glisseMinuteurRef.current);
    glisseMinuteurRef.current = window.setTimeout(() => {
      glisseRef.current = false;
      glisseMinuteurRef.current = null;
    }, GLISSE_MS);
  }

  /**
   * La montee d'un envoi, menee a la main.
   *
   * Le fil monte pendant que la bulle sort du composeur, sur la meme duree
   * et la meme courbe (`ms-part`) : les deux mouvements n'en font qu'un.
   * `behavior: 'smooth'` laissait au navigateur sa duree et sa courbe a
   * lui, et ne partait qu'une fois la bulle deja posee.
   *
   * Le bas se relit a chaque image : le message revient de la base pendant
   * la montee, et une photo peut grandir dessous. Une montee en cours n'est
   * donc pas relancee, elle suit. De loin dans l'historique, la course est
   * longue : elle prend alors le temps de la descente ordinaire.
   */
  function monterAuBas() {
    const view = scrollRef.current;
    if (!view || monteeRef.current) return;
    arreterLaDescente();

    const depart = view.scrollTop;
    const course = Math.max(0, view.scrollHeight - view.clientHeight) - depart;
    if (course <= 0) return;
    const duree = course > view.clientHeight ? Math.min(DESCENTE_MAX_MS, 320 + course / 3) : ENVOI_MS;
    const debut = performance.now();
    monteeRef.current = true;

    const pas = (maintenant: number) => {
      const t = Math.min(1, (maintenant - debut) / duree);
      /* Vive au depart, posee a l'arrivee : la courbe de `ms-part`. */
      const e = 1 - Math.pow(1 - t, 5);
      const arrivee = Math.max(0, view.scrollHeight - view.clientHeight);
      view.scrollTop = depart + (arrivee - depart) * e;
      if (t < 1) {
        descenteRef.current = requestAnimationFrame(pas);
        return;
      }
      descenteRef.current = null;
      monteeRef.current = false;
      noteReadingPlace();
    };
    descenteRef.current = requestAnimationFrame(pas);
  }

  /**
   * Le geste de la pastille : redescendre au dernier message.
   *
   * La descente est menee a la main plutot que par `behavior: 'smooth'`.
   * Le navigateur y met une duree a lui, la meme pour dix pixels et pour
   * dix mille : d'un bout a l'autre d'une longue conversation, cela ne se
   * distingue plus d'un saut. Ici la duree suit la distance — on voit le
   * fil defiler, et on comprend d'ou l'on revient.
   *
   * Bornee tout de meme : passe une seconde, on n'admire plus, on attend.
   */
  function allerAuBas() {
    const view = scrollRef.current;
    if (!view) return;
    const brut = data?.thread.at(-1)?.createdAt;
    vuRef.current = brut ? Date.parse(brut) : 0;
    gapRef.current = 0;
    setEnBas(true);
    setNouveaux(0);

    const depart = view.scrollTop;
    const arrivee = Math.max(0, view.scrollHeight - view.clientHeight);
    const course = arrivee - depart;
    if (course <= 0) {
      view.scrollTop = arrivee;
      return;
    }

    arreterLaDescente();
    const duree = Math.min(DESCENTE_MAX_MS, 320 + course / 3);
    const debut = performance.now();

    const pas = (maintenant: number) => {
      const t = Math.min(1, (maintenant - debut) / duree);
      /* Douce au depart comme a l'arrivee : une descente qui part et
         s'arrete net donne l'impression d'un saut mal rattrape. */
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      view.scrollTop = depart + course * e;
      descenteRef.current = t < 1 ? requestAnimationFrame(pas) : null;
    };
    descenteRef.current = requestAnimationFrame(pas);
  }

  /* Le doigt reprend la main a tout moment : on n'emmene personne de
     force. */
  function arreterLaDescente() {
    monteeRef.current = false;
    if (descenteRef.current === null) return;
    cancelAnimationFrame(descenteRef.current);
    descenteRef.current = null;
  }

  function noteReadingPlace() {
    if (performance.now() - resizedRef.current < 250) return;
    /* Pendant la montee, l'ecart au bas ne dit rien de la lecture : il
       aurait fait paraitre la pastille le temps d'un envoi. */
    if (monteeRef.current) return;

    const view = scrollRef.current;
    if (!view) return;
    /*
      L'ecart ne descend jamais sous zero.

      WebKit laisse le defilement passer sous le bas — au rebond, et
      surtout quand la vue grandit d'un coup parce que le clavier s'en
      va : la position reste ou elle etait, alors que le maximum, lui, a
      baisse. L'ecart devenait alors negatif, et la remise en place s'en
      servait pour pousser le fil *encore* plus loin sous le bas. On
      voyait la conversation collee en haut et un grand vide dessous.
    */
    gapRef.current = Math.max(0, view.scrollHeight - view.clientHeight - view.scrollTop);
    hautRef.current = view.scrollTop;
    // Retenue hors du composant : c'est au remontage qu'elle sert. La date
    // du dernier message part avec, pour savoir au retour si l'on a manque
    // quelque chose.
    const brut = data?.thread.at(-1)?.createdAt;
    const dernier = brut ? Date.parse(brut) : 0;
    // Revenu au bas : tout est vu, la pastille n'a plus rien a dire.
    if (gapRef.current <= COLLE_PX) vuRef.current = dernier;
    placesDeLecture.set(conversationId, {
      ecart: gapRef.current,
      dernier,
      haut: view.scrollTop,
    });
    annoncer();
  }

  /*
    Poser le doigt sur le fil ne chasse pas le clavier.

    WebKit deplace pourtant le focus au moindre appui hors du champ, ce
    qui le renvoyait des qu'on touchait une bulle. On le lui refuse :
    `preventDefault()` sur `mousedown` empeche ce deplacement, sans rien
    couper d'autre. Le defilement n'en depend pas, l'appui long passe par
    les evenements pointeur, et les elements qui reagissent au clic sont
    laisses de cote.

    Le renvoi au glissement, lui, ne se joue pas ici : des que WebKit
    prend la main pour faire defiler, il cesse d'envoyer les `touchmove` a
    la page. Un geste suivi depuis le document ne recevait donc que ses
    tout premiers points, le doigt encore loin du composeur, et ne se
    declenchait jamais. C'est `AppDelegate.swift` qui l'observe.
  */
  useEffect(() => {
    const view = scrollRef.current;
    if (!view) return;

    function keepFocus(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Element && target.closest('button, a, img, input, textarea')) {
        return;
      }
      event.preventDefault();
    }

    view.addEventListener('mousedown', keepFocus);
    return () => view.removeEventListener('mousedown', keepFocus);
  }, []);

  const conversation = data?.conversation ?? null;
  const other = conversation?.members.find((member) => member.id !== user?.id) ?? null;
  const isGroup = conversation?.type === 'group';

  /*
    Le statut du chargement fige l'instant d'ouverture : l'autre pouvait
    partir, « En ligne » restait. La surveillance le tient a jour — et
    tant qu'elle n'a pas parle, on affiche ce que le chargement savait.
  */
  const [enLigne, setEnLigne] = useState<boolean | null>(null);
  useEffect(() => {
    if (isGroup || !other?.id) return;
    setEnLigne(null);
    return usersApi.watchPresence(other.id, setEnLigne);
  }, [isGroup, other?.id]);
  const otherEnLigne = enLigne ?? other?.online ?? false;
  /* Les membres, ranges par identifiant : le fil signe chaque salve, et
     la chercher dans un tableau a chaque bulle serait du gachis. */
  const auteurs = useMemo(
    () =>
      isGroup && conversation
        ? new Map(conversation.members.map((membre) => [membre.id, membre]))
        : null,
    [isGroup, conversation],
  );

  /* Ce qui a empeche le dernier envoi, le temps d'etre lu. */
  const [echec, setEchec] = useState<string | null>(null);
  /*
    Le message unique d'une demande vient de partir.

    `canWrite` ne retombe qu'apres la relecture, et deux envois lances
    coup sur coup lisaient tous deux l'etat d'avant : on en glissait un
    second dans l'intervalle. La porte se ferme donc ici, des l'envoi
    accepte, sans attendre que le serveur le confirme a l'ecran.
  */
  const [demandeEnvoyee, setDemandeEnvoyee] = useState(false);
  useEffect(() => {
    if (!echec) return;
    const t = setTimeout(() => setEchec(null), 4000);
    return () => clearTimeout(t);
  }, [echec]);

  /*
    La saisie grandit avec ce qu'on ecrit, et le fil monte avec elle :
    voir `useSaisieExtensible`. La retaille est datee comme une autre : le
    defilement qu'elle provoque ne doit pas se relire comme un geste du
    membre.
  */
  useSaisieExtensible(saisieRef, draft, () => scrollRef.current, () => {
    resizedRef.current = performance.now();
  });

  /*
    Lire la conversation vaut lire ses notifications : elles quittent le
    haut de l'ecran a l'ouverture, et au retour dans l'application si l'on
    y etait reste. Au premier plan, le systeme n'en ajoute aucune.
  */
  useEffect(() => {
    void effacerLesNotificationsDe(conversationId);
    const auRetour = () => {
      if (document.visibilityState === 'visible') void effacerLesNotificationsDe(conversationId);
    };
    document.addEventListener('visibilitychange', auRetour);
    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [conversationId]);

  async function onSend(event: FormEvent) {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;
    if (enModif) {
      await enregistrerLaModif(enModif, content);
      return;
    }
    setSending(true);
    try {
      preparerLEnvoi();
      await messages.send(conversationId, content, null, null, null, null, repondA?.id ?? null);
      setDraft('');
      garderLeBrouillon(monId, conversationId, '');
      setRepondA(null);
      messages.arreterFrappe(conversationId);
      if (conversation?.request === 'sent') setDemandeEnvoyee(true);
      suivreSonEnvoi();
      reload();
    } catch (cause) {
      /*
        Un envoi refuse ne disait rien.

        Il n'y avait pas de `catch` : le refus partait en promesse non
        rattrapee, le champ gardait son texte sans qu'on sache pourquoi, et
        la bulle posee d'avance par le SDK restait a l'ecran une seconde.
        On croyait avoir envoye plusieurs messages la ou aucun n'etait
        parti.

        Le texte reste dans le champ — c'est le sien, on ne le jette pas —
        et la raison s'affiche.
      */
      setEchec(
        cause instanceof Error ? cause.message : t.messages.conversation.pasParti,
      );
      reload();
    } finally {
      setSending(false);
    }
  }

  /*
    L'enregistrement en cours.

    L'enregistreur vit dans une reference : il n'a rien a voir avec le
    rendu, et le remettre a chaque image relancerait le micro. L'etat, lui,
    ne porte que ce qui se dessine — le mode, le temps, l'onde, et le
    survol de la corbeille.
  */
  const recorderRef = useRef<VoiceRecording | null>(null);
  const startedAtRef = useRef(0);
  const pressedAtRef = useRef(0);
  const trashRef = useRef<HTMLButtonElement>(null);

  /**
   * Deux facons d'enregistrer, et donc deux etats.
   *
   * `pressing` : le doigt est encore sur le micro, on ne sait pas encore
   * ce qu'il veut. `tap` : c'etait un appui bref, l'enregistrement
   * continue seul et partira au bouton d'envoi.
   *
   * L'appui maintenu n'a pas d'etat a lui : c'est la duree de l'appui,
   * mesuree au relacher, qui le distingue du bref. Une bascule au bout
   * d'un delai demandait un minuteur a annuler dans quatre chemins
   * differents — et c'est en oubliant de la declencher que l'appui
   * maintenu ne faisait rien du tout.
   */
  const [mode, setMode] = useState<'pressing' | 'tap' | null>(null);
  const [recordingMs, setRecordingMs] = useState(0);
  const [enPause, setEnPause] = useState(false);
  const [levels, setLevels] = useState<number[]>([]);
  const [overTrash, setOverTrash] = useState(false);
  const recording = mode !== null;

  /*
    Le battement de l'enregistrement : le compteur, l'onde, et l'arret
    d'office.

    Un seul intervalle pour les trois. Le temps se relit sur l'horloge
    plutot que de s'accumuler, sinon un intervalle retarde par le systeme
    ferait deriver le compteur.

    Un vocal se plafonne (`MAX_VOICE_SECONDS`) : au-dela, la data URL ne
    passerait plus dans la base. On envoie alors ce qui est enregistre
    plutot que de tout perdre — la limite est une fin, pas une punition.
  */
  useEffect(() => {
    if (!recording) return;

    const tick = window.setInterval(() => {
      const enregistreur = recorderRef.current;
      /*
        Le temps vient de l'enregistreur, pas d'une soustraction depuis le
        debut : une pause ne doit pas s'y compter. L'onde, elle, se fige —
        des barres qui defilent alors que rien n'est capte mentiraient.
      */
      const elapsed = enregistreur?.duree() ?? Date.now() - startedAtRef.current;
      setRecordingMs(elapsed);
      if (!enregistreur?.enPause()) {
        setLevels((current) => [...current, enregistreur?.level() ?? 0].slice(-LIVE_BARS));
      }

      if (elapsed >= MAX_VOICE_SECONDS * 1000) void finishRecording(true);
    }, LIVE_STEP_MS);

    return () => window.clearInterval(tick);
  }, [recording]);

  /** Ouvre le micro. Le mode reste indecis tant que le doigt est pose. */
  async function beginRecording() {
    if (recording) return;
    setNotice(null);

    try {
      recorderRef.current = await startRecording();
      startedAtRef.current = Date.now();
      setRecordingMs(0);
      setLevels([]);
      setOverTrash(false);
      setEnPause(false);
      setMode('pressing');
    } catch (cause) {
      flash(cause instanceof Error ? cause.message : t.messages.conversation.microIndisponible);
    }
  }

  /** `send` a faux : on jette l'enregistrement sans rien envoyer. */
  async function finishRecording(send: boolean) {
    const recorder = recorderRef.current;
    recorderRef.current = null;

    /*
      La barre s'en va avec l'enregistrement, sans repli vers le micro :
      un vocal fini est fini, et voir la barre s'ecraser vers la droite
      retenait l'oeil sur ce qui venait de se terminer.
    */
    setMode(null);
    setOverTrash(false);
    if (!recorder) return;

    if (!send) {
      recorder.cancel();
      return;
    }

    setSending(true);
    try {
      const clip = await recorder.stop();
      preparerLEnvoi();
      await messages.send(conversationId, '', null, null, clip);
      suivreSonEnvoi();
      reload();
    } catch (cause) {
      flash(cause instanceof Error ? cause.message : t.messages.conversation.vocalImpossible);
    } finally {
      setSending(false);
    }
  }

  /*
    Le doigt se leve, et c'est la que tout se decide.

    Un appui bref laisse l'enregistrement continuer seul : c'est le bouton
    d'envoi qui le terminera. Un appui maintenu, lui, se termine en
    lachant — envoye, ou jete si le doigt est arrive sur la corbeille.

    La corbeille l'emporte dans les deux cas : y avoir glisse le doigt est
    une intention trop claire pour qu'un appui bref la contredise.
  */
  function onMicRelease() {
    if (mode !== 'pressing') return;

    if (overTrash) {
      void finishRecording(false);
      return;
    }

    if (Date.now() - pressedAtRef.current < TAP_MS) {
      setMode('tap');
      return;
    }
    void finishRecording(true);
  }

  /*
    Jeter a la corbeille : on suit le doigt jusqu'a elle plutot que
    d'attendre un seuil de distance. La cible est la ou elle se voit, et
    la marge de 24 px la rend atteignable au pouce.
  */
  function onMicMove(event: PointerEvent<HTMLButtonElement>) {
    if (mode !== 'pressing') return;

    const trash = trashRef.current?.getBoundingClientRect();
    setOverTrash(
      Boolean(
        trash &&
        /* La corbeille est du cote ou l'on commence a lire : a droite en arabe. */
        (sensX === 1 ? event.clientX < trash.right + 24 : event.clientX > trash.left - 24) &&
        event.clientY > trash.top - 24 &&
        event.clientY < trash.bottom + 24,
      ),
    );
  }

  /** Une photo part seule : le brouillon en cours n'est pas emporte avec. */
  async function onSendPhoto(imageUrl: string) {
    setSending(true);
    setNotice(null);
    try {
      preparerLEnvoi();
      await messages.send(conversationId, '', null, imageUrl);
      suivreSonEnvoi();
      reload();
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.messages.conversation.envoiImpossible);
    } finally {
      setSending(false);
    }
  }

  /*
    Corriger un message : son texte passe dans le champ, et le rappel
    au-dessus dit ce qu'on est en train de faire. Une reponse en cours
    s'efface — on ne repond pas en corrigeant.
  */
  function commencerLaModif(message: Message) {
    fermerLaFeuilleDUnCoup();
    if (sortieRef.current !== null) {
      window.clearTimeout(sortieRef.current);
      sortieRef.current = null;
    }
    setRepondPart(false);
    setRepondA(null);
    if (!enModif) brouillonGardeRef.current = draft;
    setEnModif(message);
    setDraft(message.content);
    messages.arreterFrappe(conversationId);
    /* Le toucher de « Modifier » est le geste qui ouvre le clavier. */
    autoriserLeClavier();
    saisieRef.current?.focus();
  }

  /* Le champ retrouve ce qu'on y ecrivait avant de corriger. */
  function finirLaModif() {
    setEnModif(null);
    setDraft(brouillonGardeRef.current);
    brouillonGardeRef.current = '';
  }

  async function enregistrerLaModif(message: Message, content: string) {
    if (content === message.content.trim()) {
      finirLaModif();
      return;
    }
    setSending(true);
    try {
      await messages.modifierMessage(conversationId, message.id, content);
      finirLaModif();
      reload();
    } catch (cause) {
      setEchec(cause instanceof Error ? cause.message : t.messages.conversation.pasModifie);
    } finally {
      setSending(false);
    }
  }

  async function onCopy(message: Message) {
    fermerLaFeuilleDUnCoup();
    try {
      await navigator.clipboard.writeText(message.content);
      flash(t.messages.conversation.copie);
    } catch {
      // Le presse-papiers est refuse hors HTTPS : on le dit.
      flash(t.composants.partage.copieImpossible);
    }
  }

  async function onDelete(message: Message) {
    fermerLaFeuilleDUnCoup();
    try {
      await messages.removeMessage(conversationId, message.id);
      reload();
    } catch (cause) {
      flash(cause instanceof Error ? cause.message : t.fil.commun.suppressionImpossible);
    }
  }

  function flash(message: string) {
    setNotice(message);
    setTimeout(() => setNotice(null), 2600);
  }

  /**
   * Appui long : n'ouvre la feuille que s'il y a une action a proposer.
   * Sur la photo d'un autre membre — rien a copier, rien a supprimer —
   * elle ne montrait qu'un bouton "Annuler", donc rien du tout.
   */
  function onHold(message: Message, trigger: HTMLElement) {
    /*
      La feuille s'ouvre desormais sur tout message vivant.

      Elle ne s'ouvrait que s'il y avait un texte a copier ou un message a
      soi a supprimer : la photo d'un autre, son vocal, ne repondaient pas
      au doigt. Reagir se pose sur n'importe quoi, donc il y a toujours
      quelque chose a faire. Restent dehors ce qui n'a pas d'auteur — les
      lignes de service et la trace d'un appel : on ne repond pas a un
      fait.
    */
    if (message.deleted || message.systeme || message.call) return;

    /*
      Le clavier s'ecarte le temps du choix, puis revient.

      La feuille se pose sur le message, et le clavier lui prenait la
      moitie de l'ecran — on choisissait un emoji dans ce qui restait. Il
      se retire donc a l'ouverture, et on le rend au membre des qu'il a
      choisi : il n'a jamais gene, et rien n'est perdu de ce qu'il
      ecrivait.
    */
    if (sortieMenuRef.current !== null) {
      window.clearTimeout(sortieMenuRef.current);
      sortieMenuRef.current = null;
    }
    if (origineRef.current) origineRef.current.style.visibility = '';
    origineRef.current = null;
    setMenuPart(false);
    /*
      Le focus ne suffit pas a dire qu'on ecrivait.

      Un clavier baisse au doigt laisse le champ focalise : `activeElement`
      affirmait alors qu'on ecrivait, et poser un emoji faisait remonter un
      clavier que personne n'avait demande. La reserve du bas, elle, ne
      ment pas — elle vaut zero des que le clavier est parti.
    */
    /* On ne rendra le clavier que si c'est nous qui l'avons pris : le
       drapeau dit exactement cela, et rien d'autre. */
    ecrivaitRef.current = document.activeElement === saisieRef.current;
    if (ecrivaitRef.current) saisieRef.current?.blur();

    /* La rangee entiere, pas la seule bulle : c'est elle qui porte le
       cote, le nom de l'auteur et les emojis deja poses. */
    const rangee = trigger.closest('.ms-msg') as HTMLElement | null;
    if (!rangee) return;

    /*
      C'est le message qu'on souleve, pas un double.

      La copie se posait sur l'original, qui restait la : on voyait le
      meme message deux fois des que la colonne se decalait pour tenir
      dans l'ecran. L'original s'efface donc le temps du soulevement, et
      revient quand la copie a fini de le reposer.
    */
    /* Le dessin d'abord, l'effacement ensuite : pris dans l'autre ordre,
       la copie emportait le style qui cache l'original et disparaissait
       avec lui. */
    const dessin = { rect: rangee.getBoundingClientRect(), html: rangee.outerHTML };
    origineRef.current = rangee;
    rangee.style.visibility = 'hidden';
    setHeld({ message, ...dessin });
  }

  /**
   * Ramener le fil sur un message, et le faire remarquer.
   *
   * Le defilement seul ne suffit pas : on arrive quelque part sans
   * savoir lequel, parmi trois bulles voisines, etait le bon. Une
   * lueur breve le designe, puis s'efface — elle a dit ce qu'elle avait
   * a dire.
   */
  function allerAuMessage(messageId: string, comment: ScrollBehavior = 'smooth') {
    const view = scrollRef.current;
    const cible = view?.querySelector<HTMLElement>(`[data-message="${messageId}"]`);
    if (!view || !cible) {
      setNotice(t.messages.conversation.plusCharge);
      return;
    }

    /*
      On calcule la place, on n'appelle pas `scrollIntoView`.

      Celui-ci fait defiler tout ce qui contient la cible, et pas
      seulement le fil : clavier monte, WebKit decalait la vue web entiere
      pour la degager, et il restait un vide entre la saisie et le
      clavier. Ici on ne touche qu'a notre propre defilement, qui est le
      seul concerne.

      La difference des deux rectangles donne la position de la cible dans
      le fil sans rien supposer de la mise en page — ni marges, ni parent
      positionne.
    */
    const ecart = cible.getBoundingClientRect().top - view.getBoundingClientRect().top;
    const centre = view.scrollTop + ecart - (view.clientHeight - cible.offsetHeight) / 2;
    view.scrollTo({
      top: Math.max(0, Math.min(centre, view.scrollHeight - view.clientHeight)),
      behavior: comment,
    });

    /*
      A l'arrivee, le fil est pose d'un coup, et il doit y rester : ce qui
      tient le bas — un media qui se charge, une carte qui se remplit — lit
      l'ecart avant que le defilement ne l'ait mis a jour. On le lui donne
      donc tout de suite, et l'on decide pour le doigt ou l'on regarde.
    */
    if (comment === 'auto') {
      doigtRef.current = true;
      gapRef.current = Math.max(0, view.scrollHeight - view.clientHeight - view.scrollTop);
      hautRef.current = view.scrollTop;
    }

    cible.classList.add('ms-msg--designe');
    window.setTimeout(() => cible.classList.remove('ms-msg--designe'), 1400);
  }

  /**
   * Choisir le message auquel on repond, et ouvrir le clavier.
   *
   * Le focus est pose ici, dans la foulee du doigt qui se leve, et non
   * dans un effet declenche par l'etat : WebKit n'ouvre le clavier que
   * pendant un geste du membre, et un focus arrive au rendu suivant
   * n'en est plus un. Sans cela le bandeau apparaissait et l'on se
   * retrouvait devant un champ qu'il fallait aller toucher.
   */
  function choisirRepondre(message: Message) {
    if (enModif) finirLaModif();
    if (sortieRef.current !== null) {
      window.clearTimeout(sortieRef.current);
      sortieRef.current = null;
    }
    setRepondPart(false);
    setRepondA(message);
    /* Tirer un message pour y repondre : le second des deux gestes. */
    autoriserLeClavier();
    saisieRef.current?.focus();
  }

  /**
   * Pose ou retire son emoji sur un message.
   *
   * La feuille se referme avant l'ecriture : la pastille arrivera par
   * l'ecoute, comme celle d'un autre. Rien a rejouer ici, et pas de
   * demi-seconde ou la feuille reste ouverte sur un geste deja fait.
   */
  async function onReagir(message: Message, emoji: string | null) {
    refermerLaFeuille();
    try {
      await messages.reagir(conversationId, message.id, emoji);
    } catch {
      setNotice(t.messages.conversation.reactionEchec);
    }
  }

  /*
    Le double appui bascule le coeur : il le pose, ou le retire s'il y
    est deja. Un autre emoji a moi sur ce message est remplace par le
    coeur — chacun n'en pose qu'un par message.
  */
  async function aimer(message: Message) {
    const dejaAime = reactions[message.id]?.[monId] === COEUR;
    try {
      await messages.reagir(conversationId, message.id, dejaAime ? null : COEUR);
    } catch {
      setNotice(t.messages.conversation.reactionEchec);
    }
  }

  // Le fil ne glisse qu'a l'aller : voir `SubScreen`.
  const entrant = useNavigationType() === 'PUSH';

  return (
    <div
      className={`ms-thread${entrant ? ' ms-thread--enter' : ''}${
        /* Ouverte depuis un message trouve : on arrive plus posement. */
        entrant && vise ? ' ms-thread--enter-lent' : ''
      }`}
      {...swipe}
    >
      {/*
        Le voile du haut, en couches de flou decroissant. Quatre calques
        vides plutot qu'un seul : voir `.ms-veil`, c'est ce qui fait
        disparaitre la demarcation.
      */}
      <span className="ms-veil" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </span>

      <header className="ms-thread__bar">
        <button className="ms-thread__back" aria-label={t.commun.retour} onClick={() => navigate(-1)}>
          <CaretLeft className="sens-x" size={22} weight="bold" />
        </button>

        {/*
          L'en-tete est le raccourci vers le detail : un groupe ouvre sa
          fiche, une discussion privee ouvre le profil du membre. Plus
          besoin d'un menu a trois points.
        */}
        <button
          className="ms-thread__identity-btn"
          onClick={() =>
            navigate(
              isGroup ? `/app/messages/${conversationId}/info` : `/app/members/${other?.id ?? ''}`,
            )
          }
          aria-label={
            isGroup ? t.messages.commun.infosGroupe : t.messages.conversation.voirProfil(conversation?.title ?? '')
          }
        >
          {isGroup ? (
            <GroupAvatar size={38} rounded photoUrl={conversation?.avatarUrl ?? null} />
          ) : (
            <Avatar user={other ? { ...other, online: otherEnLigne } : other} size={38} showOnline />
          )}

          <span className="ms-thread__identity">
            <span className="ms-thread__name">
              {conversation?.title ?? ''}
              {!isGroup && <BadgeEmoji user={other} />}
            </span>
            {/*
              « Il écrit… » prend la place de « En ligne » : les deux
              disent la meme chose, et le second est plus precis. Dans un
              groupe, c'est le nombre qui parle — nommer trois personnes
              ferait deborder la barre.
            */}
            {frappent.length > 0 ? (
              <span className="ms-thread__online ms-thread__typing">
                {isGroup && frappent.length > 1
                  ? t.messages.conversation.plusieursEcrivent(frappent.length)
                  : t.messages.commun.ecrit}
              </span>
            ) : (
              !isGroup && otherEnLigne && <span className="ms-thread__online">{t.commun.enLigne}</span>
            )}
          </span>
        </button>

        {/*
          Appeler depuis le fil : c'est la qu'on se parle, et remonter au
          profil pour lancer l'appel n'avait pas de sens.

          Mais pas la meme condition qu'ecrire : `canWrite` s'ouvre aussi
          sur une demande de message, une rencontre a une sortie, un fil
          deja commence. Un inconnu pouvait donc faire sonner un telephone.
          Seule l'amitie ouvre l'appel — et on n'appelle pas un groupe.

          Le compte de l'application non plus : il repond par ecrit, et
          il porte deja des amities d'avant cette regle, qui verraient
          sinon un combine que la couche donnee refuse.
        */}
        {!isGroup && other && !other.officiel && conversation?.ami && (
          <>
            <IconButton
              className="ms-thread__call"
              label={t.messages.conversation.appelerVideo(conversation.title)}
              variant="ghost"
              size={38}
              disabled={occupe}
              onClick={() =>
                void appeler(
                  { id: other.id, username: conversation.title, avatarUrl: other.avatarUrl },
                  true,
                )
              }
            >
              <CameraArrondie />
            </IconButton>

            <IconButton
              className="ms-thread__call"
              label={t.messages.conversation.appeler(conversation.title)}
              variant="ghost"
              size={38}
              disabled={occupe}
              onClick={() =>
                void appeler({
                  id: other.id,
                  username: conversation.title,
                  avatarUrl: other.avatarUrl,
                })
              }
            >
              <Phone size={22} />
            </IconButton>
          </>
        )}
      </header>

      {/*
        Toucher la conversation referme le clavier ; la faire defiler,
        non. C'est la difference entre poser le doigt et le promener :
        on retient l'endroit et l'instant du contact, et on ne referme
        que si rien n'a bouge. Un appui sur une bulle, un lien ou un
        bouton garde son propre role — il y a deja quelque chose a y
        faire.
      */}
      <div
        className="scroll-area ms-thread__scroll"
        ref={scrollRef}
        /* De quoi faire monter le dernier message au-dessus du rappel de
           reponse, qui le recouvrait sans qu'on puisse aller le lire. */
        style={
          {
            '--rappel': (repondA && !repondPart) || enModif ? `${HAUTEUR_RAPPEL_PX}px` : '0px',
          } as React.CSSProperties
        }
        onScroll={noteReadingPlace}
        onPointerDown={(evenement) => {
          arreterLaDescente();
          contactRef.current = {
            x: evenement.clientX,
            y: evenement.clientY,
            a: performance.now(),
          };
        }}
        onPointerUp={(evenement) => {
          const contact = contactRef.current;
          contactRef.current = null;
          if (!contact) return;
          if (document.activeElement !== saisieRef.current) return;
          if (performance.now() - contact.a > APPUI_MS) return;
          if (
            Math.abs(evenement.clientX - contact.x) > APPUI_PX ||
            Math.abs(evenement.clientY - contact.y) > APPUI_PX
          ) {
            return;
          }
          const cible = evenement.target;
          if (cible instanceof Element && cible.closest('button, a, input, textarea')) return;
          saisieRef.current?.blur();
        }}
      >
        {/*
          Le contenu du fil, que le geste deplace. La zone qui defile,
          elle, ne bouge jamais : deplacee, elle emportait sa limite, et
          les messages restaient rognes net a l'ancienne pendant le
          rebond. Voir `usePullToRefresh`, option `zoneFixe`.
        */}
        <div className="ms-thread__contenu" ref={contenuRef}>
        {loading && <ScreenLoader />}
        {error && <ScreenError message={error} onRetry={reload} />}
        {/*
          Ce que devient un message, dit au debut de la conversation.

          La duree de conservation etait ecrite dans les reglages et dans
          les conditions : deux endroits ou l'on ne va pas, alors que la
          question se pose ici — en relisant, ou en cherchant quelque
          chose qu'on avait envoye. Elle se lit donc a l'endroit ou
          commence l'histoire, comme le mot de chiffrement des autres
          messageries.

          « Apres avoir ete lus » et non « apres l'envoi » : c'est la
          regle exacte du serveur, et la nuance compte pour qui attend une
          reponse a un message parti il y a huit jours.
        */}
        {data && (
          <p className="ms-conservation">
            {t.messages.conversation.effacement}
          </p>
        )}
        {data && (
          <Thread
            thread={data.thread}
            myId={user?.id ?? ''}
            partDepuis={partDepuis}
            auteurs={auteurs}
            autre={other}
            onHold={onHold}
            onOpenPhoto={(src, from) => setViewed({ src, from })}
            onRepondre={choisirRepondre}
            reactions={reactions}
            onAllerA={allerAuMessage}
            onVoirReactions={setReacteurs}
            onAimer={(message) => void aimer(message)}
          />
        )}
        </div>
        {/*
          Une borne immobile apres le contenu : deplace vers le haut, il
          ne doit pas raccourcir la course du defilement, sinon le fil
          sauterait d'autant au bas pendant le rebond.
        */}
        <span className="ms-thread__fin" aria-hidden="true" />
      </div>

      {/*
        La fleche du geste de reponse, une pour tout le fil.

        Elle etait dans chaque message : cent elements masques dans la
        page, et un masque se rasterise a part — de quoi peser sur tout
        ce qui defile, qu'on glisse ou non. On n'en tire jamais deux a la
        fois, donc une seule suffit ; le geste la pose a hauteur du
        message tenu et ne lui ecrit plus que son opacite.
      */}
      <span className="ms-fleche" aria-hidden>
        <ArrowUUpLeft className="sens-x" size={20} />
      </span>

      {notice && <p className="ms-thread__notice">{notice}</p>}

      {/*
        L'historique reste lisible, mais on n'ecrit plus a quelqu'un qui
        n'est pas — ou n'est plus — un ami.
      */}
      {/*
        L'avertissement, avant le premier message et non apres.

        Ecrire a quelqu'un qu'on ne connait pas n'ouvre qu'une porte
        entrebaillee : un message, et rien tant qu'il n'a pas repondu. Le
        dire une fois le message parti serait le dire trop tard — on
        l'aurait ecrit en croyant pouvoir se reprendre.
      */}
      {conversation?.request === 'sent' && conversation.canWrite && !demandeEnvoyee && (
        <p className="ms-thread__heads-up">
          {t.messages.conversation.pasAmis}
        </p>
      )}

      {/*
        La pastille flotte sur une fente sans hauteur, posee juste avant le
        composeur : c'est ce qui la place dix pixels au-dessus de lui sans
        rien retirer au fil, et sans dependre de la hauteur du composeur,
        qui grandit avec le texte.
      */}
      {/* Elle passe au-dessus du rappel de reponse, qui occupe desormais
          la place ou elle flottait. Elle redescend avec lui. */}
      <div
        className={`ms-jump-slot${(repondA && !repondPart) || enModif ? ' ms-jump-slot--haute' : ''}`}
        aria-hidden={enBas}
      >
        {!enBas && (
          <button
            className="ms-jump"
            /*
              Redescendre ne rend pas le clavier : WebKit deplace le focus
              au moindre appui hors du champ, et la vue regagnait alors
              trois cents pixels en pleine descente. Meme geste que sur le
              bouton d'envoi et sur le fil.
            */
            onMouseDown={(event) => event.preventDefault()}
            onClick={allerAuBas}
            aria-label={
              nouveaux > 0
                ? t.messages.conversation.nouveaux(nouveaux)
                : t.messages.conversation.dernierMessage
            }
          >
            <CaretDown size={17} weight="bold" />
            {nouveaux > 0 && (
              <span className="ms-jump__count">{nouveaux > 99 ? '99+' : nouveaux}</span>
            )}
          </button>
        )}
      </div>

      {echec && <p className="ms-thread__echec">{echec}</p>}

      {conversation && (!conversation.canWrite || demandeEnvoyee) ? (
        <p className="ms-thread__closed">
          {conversation.request === 'sent' || demandeEnvoyee
            ? t.messages.erreurs.demandeEnAttente
            : t.messages.conversation.plusAmis}
        </p>
      ) : (
        <form className="ms-composer" ref={composerRef} onSubmit={onSend}>
          {/*
            Ce a quoi l'on repond, rappele juste au-dessus de la saisie.

            Sans ce rappel, le geste se perdrait : on tire un message, on
            ecrit trois lignes, et plus rien ne dit a qui l'on repondait.
            La croix defait le choix sans effacer ce qu'on a deja tape.
          */}
          {enModif && (
            /* Le meme rappel que pour une reponse : ce qu'on corrige, et
               la croix pour y renoncer sans rien changer. */
            <div className="ms-repond-fente">
              <div className="ms-repond">
                <div className="ms-repond__corps">
                  <span className="ms-repond__qui">
                    <PencilSimple size={13} weight="bold" className="ms-repond__crayon" />
                    {t.messages.conversation.modifierLeMessage}
                  </span>
                  <span className="ms-repond__texte">{enModif.content}</span>
                </div>
                <button
                  type="button"
                  className="ms-repond__annule"
                  aria-label={t.messages.conversation.annulerModif}
                  /* Le clavier reste : meme precaution que pour la reponse. */
                  onPointerDown={(evenement) => evenement.preventDefault()}
                  onMouseDown={(evenement) => evenement.preventDefault()}
                  onClick={finirLaModif}
                >
                  <X size={19} weight="bold" />
                </button>
              </div>
            </div>
          )}
          {repondA && (
            /* La fente decoupe ce qui deborde : c'est elle qui fait
               paraitre le rappel sortir de derriere le composeur, sans
               rien demander au plan de pile. */
            <div className="ms-repond-fente">
            <div className={`ms-repond${repondPart ? ' ms-repond--part' : ''}`}>
              <button
                type="button"
                className="ms-repond__corps"
                /* Le rappel dit a quoi l'on repond ; le toucher doit
                   permettre d'aller le relire. C'est la meme chose que la
                   citation posee au-dessus d'une bulle. */
                onPointerDown={(evenement) => evenement.preventDefault()}
                onMouseDown={(evenement) => evenement.preventDefault()}
                /* Aller voir, sans toucher au clavier : comme la citation
                   posee au-dessus d'une bulle. */
                onClick={() => allerAuMessage(repondA.id)}
                aria-label={t.messages.conversation.voirCite}
              >
                {/* Qui, puis quoi : sans le nom, dans un groupe, le rappel
                    ne dit qu'a moitie a quoi l'on repond. */}
                <span className="ms-repond__qui">
                  {repondA.senderId === user?.id
                    ? t.messages.commun.moi
                    : (auteurs?.get(repondA.senderId)?.username ??
                      other?.username ??
                      t.messages.commun.message)}
                </span>
                <span className="ms-repond__texte">
                  {repondA.voice ? <VoiceApercu voice={repondA.voice} /> : apercuDeCite(repondA)}
                </span>
              </button>
              <button
                type="button"
                className="ms-repond__annule"
                aria-label={t.messages.conversation.annulerReponse}
                /*
                  Le clavier ne doit pas redescendre.

                  Un bouton prend le focus des qu'on le touche, et le
                  champ le perd — donc le clavier se referme, et il faut
                  retoucher la saisie pour ecrire. La croix defait le
                  choix du message, rien d'autre : on continue d'ecrire,
                  simplement sans repondre a personne.

                  Deux precautions plutot qu'une. Refuser le defaut de
                  `pointerdown` suffit sur un ordinateur ; sous WebKit le
                  focus se joue plus tard, a la levee du doigt, et le
                  champ le perdait quand meme. On le lui rend donc dans
                  la foulee du clic — qui est un geste du membre, seul
                  moment ou le systeme accorde un clavier.
                */
                onPointerDown={(evenement) => evenement.preventDefault()}
                onMouseDown={(evenement) => evenement.preventDefault()}
                onClick={annulerLaReponse}
              >
                <X size={19} weight="bold" />
              </button>
            </div>
            </div>
          )}
          {/*
            Le champ ne quitte jamais le document, meme pendant un
            enregistrement : la barre se pose par-dessus. Le demonter
            enlevait le focus au clavier, qui redescendait aussitot — on
            revenait alors d'un vocal devant un clavier ferme.
          */}
          <div className="ms-composer__slot">
            <div
              className={`ms-composer__field${
                recording ? ' ms-composer__field--away' : ''
              }`}
            >
              <textarea
                ref={saisieRef}
                className="ms-composer__input"
                placeholder={t.messages.conversation.ecrire}
                rows={1}
                /*
                  Dans l'application, la touche retour va a la ligne, et
                  rien d'autre : on envoie avec l'avion. La plateforme
                  decide, et non ce que la page croit savoir du pointeur —
                  un telephone ne doit jamais envoyer au retour. Seul le
                  clavier d'un ordinateur, sur le site, envoie a Entree ;
                  Maj+Entree y va a la ligne.
                */
                enterKeyHint="enter"
                onKeyDown={(evenement) => {
                  if (evenement.key !== 'Enter' || evenement.shiftKey) return;
                  if (evenement.nativeEvent.isComposing) return;
                  if (Capacitor.isNativePlatform()) return;
                  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
                  evenement.preventDefault();
                  composerRef.current?.requestSubmit();
                }}
                /* Un doigt pose sur la saisie : c'est l'un des deux
                   gestes qui ouvrent le clavier. */
                onPointerDown={autoriserLeClavier}
                onFocus={(evenement) => {
                  if (autoriseRef.current) {
                    autoriseRef.current = false;
                    return;
                  }
                  evenement.currentTarget.blur();
                }}
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  /* Corriger n'est pas ecrire : rien a garder, rien a annoncer. */
                  if (enModif) return;
                  garderLeBrouillon(monId, conversationId, event.target.value);
                  if (event.target.value) messages.signalerFrappe(conversationId);
                  else messages.arreterFrappe(conversationId);
                }}
              />
            </div>

            {recording && (
              <div className={`ms-record${overTrash ? ' ms-record--dropping' : ''}`}>
                <button
                  type="button"
                  ref={trashRef}
                  className="ms-record__drop"
                  aria-label={t.messages.conversation.annulerEnregistrement}
                  onClick={() => void finishRecording(false)}
                >
                  <Trash size={19} />
                </button>

                {/*
                  Suspendre et reprendre, mains libres seulement.

                  Un appui maintenu n'en a pas besoin : lever le doigt
                  termine, et il n'y a pas de place pour un second geste.
                  C'est l'enregistrement qui continue seul qu'on peut
                  vouloir interrompre le temps de chercher ses mots.
                */}
                {mode === 'tap' && (
                  <button
                    type="button"
                    className={`ms-record__pause${enPause ? ' ms-record__pause--on' : ''}`}
                    aria-label={enPause ? t.messages.conversation.reprendre : t.composants.vocal.pause}
                    onClick={() => {
                      const enregistreur = recorderRef.current;
                      if (!enregistreur) return;
                      if (enregistreur.enPause()) enregistreur.resume();
                      else enregistreur.pause();
                      setEnPause(enregistreur.enPause());
                    }}
                  >
                    {enPause ? <Play size={17} weight="fill" /> : <Pause size={17} weight="fill" />}
                  </button>
                )}

                {/*
              L'onde vivante. Decorative : le compteur a cote dit deja ce
              qu'il y a a savoir, et quarante barres lues une a une
              n'aideraient personne.

              Les barres sont posees a droite et debordent par la gauche :
              c'est ce qui donne le defilement, sans rien deplacer.
            */}
                <span className="ms-record__wave" aria-hidden="true">
                  {levels.map((level, index) => (
                    <span
                      key={index}
                      className="ms-record__bar"
                      style={{ height: `${Math.max(10, level)}%` }}
                    />
                  ))}
                </span>

                <span className="ms-record__time">{stopwatch(recordingMs)}</span>
              </div>
            )}
          </div>

          {/*
          Un seul bouton a droite, qui change de role : le micro tant qu'il
          n'y a rien a envoyer, l'avion des qu'il y a un texte ou un
          enregistrement en cours. Deux boutons cote a cote auraient laisse
          l'un des deux inerte les trois quarts du temps.
        */}
          {/*
            La pellicule ouvre la rangee de droite : c'est le second chemin
            vers une photo ou une video, celui qui va chercher ce qui est
            deja pris. Elle s'efface pendant un enregistrement, comme tout
            ce qui n'est pas le geste en cours.
          */}
          {!recording && (
            <PhotoPicker
              preset="photo"
              label={t.messages.conversation.galerie}
              className="ms-composer__gallery"
              disabled={sending}
              onPicked={onSendPhoto}
              onError={flash}
            >
              <ImagesSquare size={20} />
            </PhotoPicker>
          )}

          {!enModif && !recording && draft.trim().length === 0 && canRecordVoice() ? (
            /*
            Le micro accepte les deux gestes sur le meme bouton : un appui
            bref lance un enregistrement qui continue seul, un appui
            maintenu enregistre tant que le doigt est la et envoie au
            lacher. `setPointerCapture` garde le doigt attache au bouton
            meme lorsqu'il glisse vers la corbeille — sans lui, le
            deplacement passe a l'element survole et le geste se perd.
          */
            <IconButton
              label={t.messages.conversation.enregistrerVocal}
              type="button"
              variant="primary"
              size={42}
              className="ms-composer__send"
              disabled={sending}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                pressedAtRef.current = Date.now();
                void beginRecording();
              }}
              onPointerMove={onMicMove}
              onPointerUp={onMicRelease}
              onPointerCancel={onMicRelease}
            >
              <Microphone size={19} weight="fill" />
            </IconButton>
          ) : mode === 'pressing' ? (
            /*
            Doigt encore pose : le bouton suit le geste et n'accepte pas de
            clic. Lacher decidera — envoyer, ou jeter si l'on est au-dessus
            de la corbeille.
          */
            <IconButton
              label={overTrash ? t.messages.conversation.lacherAnnuler : t.messages.conversation.lacherEnvoyer}
              type="button"
              variant="primary"
              size={42}
              className={`ms-composer__send${overTrash ? ' ms-composer__send--dropping' : ''}`}
              onPointerMove={onMicMove}
              onPointerUp={onMicRelease}
              onPointerCancel={onMicRelease}
            >
              <Microphone size={19} weight="fill" />
            </IconButton>
          ) : (
            <IconButton
              label={
                enModif
                  ? t.messages.conversation.modifier
                  : mode === 'tap'
                    ? t.messages.conversation.envoyerVocal
                    : t.messages.conversation.envoyer
              }
              type={mode === 'tap' ? 'button' : 'submit'}
              variant="primary"
              size={42}
              className="ms-composer__send"
              disabled={sending || (!recording && draft.trim().length === 0)}
              /*
                Appuyer ici ne rend pas le clavier.

                WebKit deplace le focus au moindre appui hors du champ :
                le clavier redescendait a chaque message envoye, et il
                fallait retoucher le champ pour ecrire le suivant. Le
                refus du deplacement se pose sur `mousedown`, qui precede
                le clic sans l'empecher — l'envoi part comme avant. Meme
                geste que sur le fil, plus haut.
              */
              onMouseDown={(event) => event.preventDefault()}
              onClick={mode === 'tap' ? () => void finishRecording(true) : undefined}
            >
              {enModif ? (
                <Check size={19} weight="bold" />
              ) : (
                <PaperPlaneTilt className="sens-x" size={19} weight="fill" />
              )}
            </IconButton>
          )}
        </form>
      )}

      {/*
        Appui long sur une bulle : les gestes d'une messagerie. La feuille
        ne s'ouvre que s'il y a quelque chose a proposer — sur la photo
        d'un autre membre, elle n'affichait qu'un bouton "Annuler".
      */}
      {/*
        Le message souleve, et ce qu'on peut en faire.

        Une feuille venue du bas aurait cache le fil et perdu le message
        qu'on designe. Ici il reste ou il etait, le reste s'efface
        derriere lui, les emojis se posent au-dessus et les actions en
        dessous — on voit ce sur quoi on agit pendant qu'on choisit.

        Le fond ferme au premier appui : c'est le geste qu'on fait sans y
        penser quand on a change d'avis.
      */}
      {held && (
        <div
          className={`ms-menu${menuPart ? ' ms-menu--part' : ''}`}
          role="dialog"
          aria-label={t.messages.conversation.actions}
          /*
            Un appui sur le fond revient d'un pas, il ne ferme pas tout.

            La palette ouverte, il la referme et rend les actions —
            Repondre, Copier, Infos — que l'on avait quittees pour aller
            chercher un emoji. Le message reprend sa place au passage,
            puisqu'il n'a plus a se serrer. Sans palette, il n'y a plus
            qu'un pas a faire : celui qui repose le message.
          */
          onClick={() => {
            if (palette && !palettePart) {
              fermerLaPalette();
              return;
            }
            refermerLaFeuille();
          }}
        >
          <div
            ref={colonneRef}
            className={`ms-menu__colonne${
              held.message.senderId === user?.id ? ' ms-menu__colonne--mien' : ''
            }${palette ? ' ms-menu__colonne--inerte' : ''}`}
          >
            <div className="ms-emojis" onClick={(evenement) => evenement.stopPropagation()}>
              {EMOJIS.map((emoji) => {
                const mien = reactions[held.message.id]?.[user?.id ?? ''] === emoji;
                return (
                  <button
                    key={emoji}
                    type="button"
                    className={`ms-emojis__choix${mien ? ' ms-emojis__choix--mien' : ''}`}
                    aria-label={mien ? t.messages.conversation.retirerReaction(emoji) : t.messages.conversation.reagirAvec(emoji)}
                    aria-pressed={mien}
                    onClick={() => void onReagir(held.message, mien ? null : emoji)}
                  >
                    {emoji}
                  </button>
                );
              })}

              {/*
                Le « + » ouvre le reste.

                Six emojis couvrent l'essentiel mais pas tout, et refuser
                le septieme aurait fait chercher ailleurs. Il est au bout
                de la rangee, la ou le pouce arrive quand aucun des six
                n'a convenu.
              */}
              <button
                type="button"
                className={`ms-emojis__plus${palette ? ' ms-emojis__plus--ouvert' : ''}`}
                aria-label={t.messages.conversation.autreEmoji}
                aria-expanded={palette}
                onClick={() => (palette ? fermerLaPalette() : setPalette(true))}
              >
                <Plus size={20} weight="bold" />
              </button>
            </div>

            {/*
              La copie du message. Inerte : on ne rejoue pas un vocal ni
              n'ouvre une photo depuis le menu, on choisit ce qu'on en
              fait. Le contenu vient de la page elle-meme, deja echappe
              par React quand il y a ete pose.
            */}
            <div
              className="ms-menu__copie"
              aria-hidden
              dangerouslySetInnerHTML={{ __html: held.html }}
            />

            {/* Les actions restent : ouvrir la palette est un pas de plus,
                pas un autre endroit — on doit pouvoir encore copier ou
                supprimer sans revenir en arriere. */}
            <div className="ms-menu__actions" onClick={(evenement) => evenement.stopPropagation()}>
              <button
                type="button"
                className="ms-menu__action"
                onClick={() => {
                  const message = held.message;
                  fermerLaFeuilleDUnCoup();
                  choisirRepondre(message);
                }}
              >
                <ArrowUUpLeft className="sens-x" size={20} />
                {t.messages.conversation.repondre}
              </button>

              {held.message.content && (
                <button
                  type="button"
                  className="ms-menu__action"
                  onClick={() => void onCopy(held.message)}
                >
                  <Copy size={20} />
                  {t.messages.conversation.copier}
                </button>
              )}

              {/* Un texte a soi, et rien d'autre : une photo, un vocal ou
                  une carte partagee ne se corrigent pas. Et seulement
                  dans le quart d'heure qui suit l'envoi. */}
              {held.message.senderId === user?.id &&
                held.message.content &&
                Date.now() - Date.parse(held.message.createdAt) <= DELAI_DE_MODIFICATION_MS &&
                !held.message.systeme &&
                !held.message.call &&
                !held.message.voice &&
                !held.message.video &&
                !held.message.imageUrl &&
                !held.message.attachedEventId &&
                !held.message.attachedPostId && (
                  <button
                    type="button"
                    className="ms-menu__action"
                    onClick={() => commencerLaModif(held.message)}
                  >
                    <PencilSimple size={20} />
                    {t.messages.conversation.modifier}
                  </button>
                )}

              {held.message.senderId === user?.id && !held.message.systeme && (
                <button
                  type="button"
                  className="ms-menu__action"
                  onClick={() => {
                    const message = held.message;
                    fermerLaFeuilleDUnCoup();
                    setInfos({ message, reperes: null });
                    void messages
                      .readCursors(conversationId!)
                      .then((reperes) => setInfos((etat) => (etat ? { ...etat, reperes } : etat)))
                      .catch(() => setInfos((etat) => (etat ? { ...etat, reperes: {} } : etat)));
                  }}
                >
                  <Info size={20} />
                  {t.messages.conversation.infos}
                </button>
              )}

              {held.message.senderId === user?.id && (
                <button
                  type="button"
                  className="ms-menu__action ms-menu__action--danger"
                  onClick={() => {
                    const message = held.message;
                    fermerLaFeuilleDUnCoup();
                    request(t.messages.conversation.supprimerQ, () => void onDelete(message));
                  }}
                >
                  <Trash size={20} />
                  {t.messages.conversation.supprimer}
                </button>
              )}
            </div>
          </div>

          {/*
            Le reste des emojis, en feuille venue du bas.

            Ranges par famille, sans recherche : un dictionnaire francais
            de sept cents noms pesait plus lourd que tout cet ecran, et
            les familles suffisent a retrouver ce qu'on cherche. La barre
            du bas saute de l'une a l'autre.
          */}
          {palette && (
            <div
              ref={paletteRef}
              className={`ms-palette${chercheEmoji ? ' ms-palette--haute' : ''}${
                palettePart ? ' ms-palette--part' : ''
              }`}
              onClick={(evenement) => evenement.stopPropagation()}
            >
              {/* La poignee : on la tire pour deplier, on la rend pour
                  refermer. Toute la bande du haut repond, pas seulement
                  le trait — un doigt ne vise pas trois pixels. */}
              <div
                className="ms-palette__poignee"
                {...prisePalette}
                onPointerCancel={prisePalette.onPointerUp}
                aria-hidden
              >
                <span />
              </div>
              {/*
                Chercher plutot que parcourir.

                Les familles etaient rangees derriere une barre d'onglets
                en bas : neuf signes a decoder, et il fallait deviner dans
                laquelle ranger ce qu'on avait en tete. Un mot va plus
                vite — et quand le champ est vide, les familles sont la,
                dans l'ordre, comme avant.

                Les mots viennent du nom Unicode de chaque signe, traduit
                par un petit dictionnaire. Ce qu'il ne connait pas reste
                en anglais, ce qui ne gene pas : « pizza » se cherche
                pareil dans les deux langues.
              */}
              <div className="ms-palette__chercher">
                <MagnifyingGlass size={17} />
                <input
                  className="ms-palette__champ"
                  placeholder={t.messages.conversation.chercherEmoji}
                  value={quete}
                  onChange={(evenement) => setQuete(evenement.target.value)}
                  ref={saisieEmojiRef}
                  onFocus={() => setChercheEmoji(true)}
                  aria-label={t.messages.conversation.chercherEmoji}
                />
                {quete && (
                  <button
                    type="button"
                    className="ms-palette__vider"
                    aria-label={t.composants.champ.effacer}
                    onClick={() => setQuete('')}
                  >
                    <X size={15} weight="bold" />
                  </button>
                )}
              </div>

              <div className="ms-palette__grille">
                {trouves.length === 0 ? (
                  <p className="ms-palette__rien">{t.messages.conversation.aucunEmoji(quete)}</p>
                ) : (
                  trouves.map((famille) => (
                    <section key={famille.nom}>
                      <p className="ms-palette__titre">{famille.nom}</p>
                      <div className="ms-palette__signes">
                        {famille.signes.map(([emoji]) => (
                          <button
                            key={emoji}
                            type="button"
                            className="ms-palette__signe"
                            aria-label={t.messages.conversation.reagirAvec(emoji)}
                            onClick={() => {
                              setPalette(false);
                              setQuete('');
                              void onReagir(held.message, emoji);
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/*
        Les infos d'un de mes messages : lu quand, remis quand. Le repere
        de lecture dit l'heure d'ouverture du fil — c'est ce que la coche
        « lu » affirme deja, ici avec l'heure.
      */}
      {/*
        Qui a pose quoi.

        La pastille disait combien, pas qui — et dans un groupe c'est la
        premiere question. Chacun avec son visage, son nom et son emoji ;
        la sienne se retire d'un appui, puisque c'est le seul endroit ou
        elle se lit comme sienne.
      */}
      <Sheet open={reacteurs !== null} onClose={() => setReacteurs(null)} large>
        <p className="cloche__titre">{t.messages.conversation.reactions}</p>
        <ul className="ms-reacteurs">
          {reacteurs &&
            Object.entries(reactions[reacteurs.id] ?? {}).map(([uid, emoji]) => {
              const moi = uid === user?.id;
              const qui = moi
                ? user
                : (auteurs?.get(uid) ?? (other?.id === uid ? other : null));
              return (
                <li key={uid}>
                  <button
                    type="button"
                    className="ms-reacteurs__ligne"
                    disabled={!moi}
                    onClick={() => {
                      setReacteurs(null);
                      void messages
                        .reagir(conversationId, reacteurs.id, null)
                        .catch(() => setNotice(t.messages.conversation.retraitEchec));
                    }}
                  >
                    <Avatar user={qui} size={40} />
                    <span className="ms-reacteurs__qui">
                      <span className="ms-reacteurs__nom">
                        {moi ? (user?.username ?? t.messages.commun.moi) : (qui?.username ?? t.commun.quelquun)}
                      </span>
                      {moi && (
                        <span className="ms-reacteurs__aide">{t.messages.conversation.appuyerSupprimer}</span>
                      )}
                    </span>
                    <span className="ms-reacteurs__emoji">{emoji}</span>
                  </button>
                </li>
              );
            })}
        </ul>
      </Sheet>

      <Sheet open={infos !== null} onClose={() => setInfos(null)} large>
        <p className="cloche__titre">{t.messages.conversation.infosMessage}</p>
        {infos && (
          <InfosMessage
            message={infos.message}
            reperes={infos.reperes}
            lecteurs={conversation?.members.filter((membre) => membre.id !== user?.id) ?? []}
          />
        )}
      </Sheet>

      <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />

      <PhotoViewer
        src={viewed?.src ?? null}
        from={viewed?.from ?? null}
        onClose={() => setViewed(null)}
      />
    </div>
  );
}

/**
 * Le contenu de la feuille « Infos » : lu quand, remis quand.
 *
 * « Remis » est l'instant du depot dans le fil — des lors, chaque
 * appareil de l'autre le recoit. « Lu » vient du repere de lecture : nul
 * si le fil n'a pas ete rouvert depuis. Dans un groupe, on compte —
 * nommer chaque lecteur ferait une liste, pas une feuille.
 */
function InfosMessage({
  message,
  reperes,
  lecteurs,
}: {
  message: Message;
  reperes: Record<string, number> | null;
  lecteurs: PublicUser[];
}) {
  const envoi = Date.parse(message.createdAt);
  const quand = (ms: number) => {
    const iso = new Date(ms).toISOString();
    const jour = dayLabel(iso);
    const dit =
      jour === t.temps.aujourdhui ? t.temps.aujourdhuiMin : jour === t.temps.Hier ? t.temps.hier : jour;
    return `${dit} ${timeOfDay(iso)}`;
  };

  if (reperes === null) {
    return <p className="cloche__vide">{t.commun.unInstant}</p>;
  }

  /*
    Sobre, comme la fiche d'un message l'exige : « Vu » et son heure,
    « Distribué » et la sienne. Dans un groupe seulement, les lecteurs
    reprennent une ligne chacun — un « Vu » anonyme n'y dirait rien.
  */
  const groupe = lecteurs.length > 1;
  const vus = lecteurs.filter((lecteur) => (reperes[lecteur.id] ?? 0) >= envoi);

  return (
    <ul className="ms-infos">
      {groupe ? (
        vus.map((lecteur) => (
          <li key={lecteur.id} className="cloche__ligne cloche__ligne--neuve">
            <Avatar user={lecteur} size={36} />
            <span className="cloche__corps">
              <span className="cloche__texte">
                <strong>{lecteur.username}</strong>
              </span>
              <span className="cloche__quand">{quand(reperes[lecteur.id] ?? envoi)}</span>
            </span>
            <Checks size={18} weight="bold" className="ms-infos__lu" />
          </li>
        ))
      ) : (
        <li className="cloche__ligne cloche__ligne--neuve">
          <span className="cloche__medaille">
            <Checks size={20} weight="bold" />
          </span>
          <span className="cloche__corps">
            <span className="cloche__texte">
              <strong>{t.messages.conversation.vu}</strong>
            </span>
            <span className="cloche__quand">
              {vus.length > 0 ? quand(reperes[vus[0].id] ?? envoi) : t.messages.conversation.pasEncore}
            </span>
          </span>
        </li>
      )}

      <li className="cloche__ligne">
        <span className="cloche__medaille ms-infos__gris">
          <Checks size={20} weight="bold" />
        </span>
        <span className="cloche__corps">
          <span className="cloche__texte">
            <strong>{t.messages.conversation.distribue}</strong>
          </span>
          <span className="cloche__quand">{quand(envoi)}</span>
        </span>
      </li>
    </ul>
  );
}

/** Le fil : un libelle a chaque changement de jour, puis les bulles. */
/**
 * Ce qu'on lit d'un message cite, en une ligne.
 *
 * Une citation n'est pas une copie : elle sert a retrouver de quoi on
 * parle. Un vocal, une photo ou un appel n'ont pas de texte a montrer —
 * on nomme alors leur nature, ce qui suffit a s'y reconnaitre.
 */
function apercuDeCite(message: Message): string {
  if (message.imageUrl) return t.messages.commun.photo;
  if (message.voice) return t.messages.commun.vocal;
  if (message.video) return t.messages.commun.video;
  if (message.attachedEventId) return t.messages.commun.sortie;
  if (message.attachedPostId) return t.messages.commun.post;
  const texte = message.content.trim();
  return texte.length > 80 ? `${texte.slice(0, 79)}…` : texte || t.messages.commun.message;
}

function Thread({
  thread,
  myId,
  partDepuis,
  auteurs,
  autre,
  onHold,
  onOpenPhoto,
  onRepondre,
  reactions,
  onAllerA,
  onVoirReactions,
  onAimer,
}: {
  thread: Message[];
  myId: string;
  /** L'heure du dernier envoi parti de cet ecran, 0 s'il n'y en a pas eu. */
  partDepuis: number;
  /** Qui est qui dans le groupe. `null` a deux : la question ne se pose pas. */
  auteurs: Map<string, PublicUser> | null;
  /** L'autre, dans une discussion a deux : les lignes de service le nomment. */
  autre: PublicUser | null;
  onHold: (message: Message, trigger: HTMLElement) => void;
  onOpenPhoto: (src: string, depuis: DOMRect) => void;
  onRepondre: (message: Message) => void;
  /** Les emojis du fil, par message puis par membre. */
  reactions: Record<string, Record<string, string>>;
  onAllerA: (messageId: string) => void;
  onVoirReactions: (message: Message) => void;
  /** Deux touches rapides sur un message : un coeur. */
  onAimer: (message: Message) => void;
}) {
  const items: ReactNode[] = [];
  /* Le fil entier est deja en main : retrouver un message cite est une
     lecture de table, pas un aller-retour a la base. */
  const parId = new Map(thread.map((message) => [message.id, message]));

  thread.forEach((message, index) => {
    const previous = index > 0 ? thread[index - 1] : null;
    const next = index < thread.length - 1 ? thread[index + 1] : null;

    if (!previous || !isSameDay(previous.createdAt, message.createdAt)) {
      items.push(
        <p className="ms-day" key={`day-${message.id}`}>
          {dayLabel(message.createdAt)}
        </p>,
      );
    }

    // La fin d'une salve du meme expediteur : c'est la que se dit « Lu »,
    // et que le visage se pose dans un groupe.
    const endsRun =
      !next || next.senderId !== message.senderId || !isSameDay(message.createdAt, next.createdAt);

    /*
      Le vocal qui s'enchaine apres celui-ci.

      Quelqu'un qui coupe son propos en trois vocaux veut qu'on l'ecoute
      d'une traite. La salve s'arrete des que le fil change de nature : un
      texte, une photo, l'autre qui repond, un autre jour. La regle est
      celle qui vient d'etre calculee pour l'heure, plus la condition que
      le suivant soit lui aussi un vocal.
    */
    const chained = !endsRun && next?.voice && message.voice ? next.id : null;

    /*
      Qui parle, en tete de salve.

      A deux, la question ne se pose pas : ce qui n'est pas de moi est de
      l'autre. A plusieurs, une bulle grise ne dit rien du tout — c'est ce
      qui rendait les groupes illisibles. Le nom et le visage ne
      reviennent qu'au changement d'expediteur ou de jour : les repeter
      sur chaque replique hacherait la salve.
    */
    const debutSalve =
      !previous ||
      previous.senderId !== message.senderId ||
      !isSameDay(previous.createdAt, message.createdAt);
    const deLautre = Boolean(auteurs) && message.senderId !== myId;
    const membre = auteurs?.get(message.senderId) ?? null;

    /*
      Ce qui est arrive au fil, et que personne n'a ecrit.

      Une ligne au milieu, jamais une bulle : ce n'est de personne. Le nom
      est celui de l'autre, quel que soit le cote d'ou l'on regarde — c'est
      la meme ligne en base, et chacun y lit son vis-a-vis.
    */
    /*
      Un depart de groupe. Le nom vient du message lui-meme : celui qui
      est parti n'est plus dans la liste des membres, et le chercher la ne
      rendrait rien.
    */
    if (
      message.systeme === 'sortie-groupe' ||
      message.systeme === 'retire-groupe' ||
      message.systeme === 'ajout-groupe'
    ) {
      const nom = message.content || membre?.username || t.commun.quelquun;
      /*
        Le retrait nomme toujours celui qui part, jamais celui qui ecrit :
        c'est l'admin qui pose la ligne, et « Tu as ete retire » ne
        s'adresse pas a lui. Le depart, lui, se tutoie — on est celui qui
        s'en va.
      */
      /*
        Trois faits, un seul dessin. L'ajout et le retrait nomment celui
        qui bouge, jamais celui qui ecrit : c'est un tiers qui pose la
        ligne. Le depart, lui, se tutoie — on est celui qui s'en va.
      */
      items.push(
        <p key={message.id} className="ms-systeme">
          {message.systeme === 'retire-groupe'
            ? t.messages.conversation.systeme.retire(nom)
            : message.systeme === 'ajout-groupe'
              ? t.messages.conversation.systeme.rejoint(nom)
              : message.senderId === myId
                ? t.messages.conversation.systeme.tuAsQuitte
                : t.messages.conversation.systeme.quitte(nom)}
        </p>,
      );
      return;
    }

    if (message.systeme === 'amitie-rompue') {
      const nom = (message.senderId === myId ? autre?.username : membre?.username) ?? t.messages.conversation.ceMembre;
      items.push(
        <p key={message.id} className="ms-systeme">
          {t.messages.conversation.systeme.plusAmi(nom)}
        </p>,
      );
      return;
    }

    items.push(
      <Bubble
        key={message.id}
        message={message}
        mine={message.senderId === myId}
        part={
          partDepuis > 0 &&
          message.senderId === myId &&
          Date.parse(message.createdAt) >= partDepuis
        }
        enGroupe={deLautre}
        auteur={deLautre && debutSalve ? membre : null}
        visage={deLautre && endsRun ? membre : null}
        finDeSalve={endsRun}
        nextVoiceId={chained}
        onHold={onHold}
        onOpenPhoto={onOpenPhoto}
        onRepondre={onRepondre}
        reactions={reactions[message.id] ?? null}
        onAllerA={onAllerA}
        onVoirReactions={onVoirReactions}
        onAimer={onAimer}
        cite={message.replyTo ? (parId.get(message.replyTo) ?? null) : null}
        citeDe={
          message.replyTo
            ? (() => {
                const source = parId.get(message.replyTo);
                if (!source) return null;
                if (source.senderId === myId) return t.messages.commun.toi;
                return auteurs?.get(source.senderId)?.username ?? autre?.username ?? null;
              })()
            : null
        }
      />,
    );
  });

  return <>{items}</>;
}

function Bubble({
  message,
  mine,
  part,
  enGroupe,
  auteur,
  visage,
  finDeSalve,
  nextVoiceId,
  onHold,
  onOpenPhoto,
  onRepondre,
  cite,
  citeDe,
  reactions,
  onAllerA,
  onVoirReactions,
  onAimer,
}: {
  message: Message;
  mine: boolean;
  /** Vient de partir d'ici : la bulle sort du composeur. */
  part: boolean;
  /** Message d'un autre dans un groupe : la gouttiere du visage est due. */
  enGroupe: boolean;
  /** Le nom, en tete de salve. */
  auteur: PublicUser | null;
  /** Le visage, au pied de la salve. */
  visage: PublicUser | null;
  /** Le dernier message d'une salve : « Lu » se dit sous lui seul. */
  finDeSalve: boolean;
  /** Le vocal a enchainer apres celui-ci, s'il y en a un. */
  nextVoiceId: string | null;
  onHold: (message: Message, trigger: HTMLElement) => void;
  onOpenPhoto: (src: string, depuis: DOMRect) => void;
  /** Le message auquel celui-ci repond, deja retrouve par l'ecran. */
  cite: Message | null;
  citeDe: string | null;
  onRepondre: (message: Message) => void;
  /** Qui a pose quoi sur ce message. */
  reactions: Record<string, string> | null;
  /** Ramener le fil sur un message, depuis une citation. */
  onAllerA: (messageId: string) => void;
  /** Montrer qui a pose quoi sur ce message. */
  onVoirReactions: (message: Message) => void;
  onAimer: (message: Message) => void;
}) {
  const { annuler, ...hold } = useLongPress((trigger) => onHold(message, trigger));

  /*
    Deux touches rapides au meme endroit : un coeur, comme partout.

    Sur ce qui a deja son geste a la touche — une photo qui s'ouvre, un
    lecteur, une citation, une pastille —, on ne double rien : la
    premiere touche y fait deja quelque chose. Rien d'autre ne s'affiche :
    la pastille arrive par l'ecoute, avec la meme animation qu'un emoji
    pose depuis la feuille.
  */
  const touche = useRef<{ a: number; x: number; y: number } | null>(null);
  function surTouche(evenement: React.MouseEvent) {
    const cible = evenement.target;
    if (cible instanceof Element && cible.closest('button, a, video, input, textarea, .ms-voice')) {
      return;
    }
    const maintenant = performance.now();
    const avant = touche.current;
    if (
      avant &&
      maintenant - avant.a < DOUBLE_TOUCHE_MS &&
      Math.hypot(evenement.clientX - avant.x, evenement.clientY - avant.y) < DOUBLE_TOUCHE_PX
    ) {
      touche.current = null;
      onAimer(message);
      return;
    }
    touche.current = { a: maintenant, x: evenement.clientX, y: evenement.clientY };
  }
  /* Le glissement previent l'appui long : sans quoi les deux gestes
     aboutissent, et le menu s'ouvre sur un message en train de partir. */
  const glisse = useGlisserPourRepondre(() => onRepondre(message), annuler);

  /* Les emojis groupes, dans l'ordre ou ils sont arrives — les cles de
     Firebase sortant par ordre alphabetique d'identifiant, cet ordre est
     stable, ce qui suffit : une pastille ne doit pas sauter de place. */
  const compte: Array<[string, number]> = [];
  for (const emoji of Object.values(reactions ?? {})) {
    const deja = compte.find(([signe]) => signe === emoji);
    if (deja) deja[1] += 1;
    else compte.push([emoji, 1]);
  }

  /*
    Ce qui arrive, ce qui s'en va.

    La comparaison se faisait pendant le rendu, dans une reference : elle
    ne voyait rien des qu'une bulle se remontait, et surtout elle ne
    pouvait rien dire des pastilles retirees — React les otait du document
    avant qu'on ait pu les animer.

    Elle passe donc par un etat, pose apres coup. Les arrivantes portent
    leur marque un demi-battement, le temps de leur animation ; les
    partantes restent affichees le temps de la leur, puis s'effacent pour
    de bon.

    La premiere passe ne compare rien : tout ce qui est la y etait avant
    nous, et ouvrir une conversation ne doit pas faire sauter ce qu'elle
    contient deja.
  */
  const connues = useRef<string[] | null>(null);
  const [neuves, setNeuves] = useState<string[]>([]);
  const [sortantes, setSortantes] = useState<string[]>([]);
  const etatDesEmojis = compte.map(([emoji, n]) => `${emoji}${n}`).join(',');

  useEffect(() => {
    const presentes = compte.map(([emoji]) => emoji);
    const avant = connues.current;
    connues.current = presentes;
    if (!avant) return;

    const arrivantes = presentes.filter((emoji) => !avant.includes(emoji));
    const parties = avant.filter((emoji) => !presentes.includes(emoji));

    if (arrivantes.length > 0) {
      setNeuves(arrivantes);
      window.setTimeout(() => setNeuves([]), POSE_EMOJI_MS);
    }
    if (parties.length > 0) {
      setSortantes(parties);
      window.setTimeout(() => setSortantes([]), RETRAIT_EMOJI_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etatDesEmojis]);

  /* Le nom ouvre la salve, le visage la ferme — de part et d'autre du
     contenu, quelle que soit sa nature : texte, photo, vocal, evenement,
     ou message retire. */
  const signature = auteur && <span className="ms-msg__auteur">{auteur.username}</span>;
  const carte = Boolean(message.attachedEventId || message.attachedPostId);
  const lu = finDeSalve && mine && message.read;

  /*
    La gouttiere n'enveloppe que ce qu'elle concerne.

    Elle avait d'abord ete posee autour de tout le monde, mes propres
    messages compris — et ceux-la passaient a gauche : leur alignement
    tient a `align-items` sur le message, qui n'atteint plus la bulle une
    fois une ligne intercalee. Hors d'un groupe, et pour ce que
    j'envoie, la structure reste donc exactement celle d'avant.
  */
  const encadrer = (contenu: ReactNode) =>
    enGroupe ? (
      <div className="ms-msg__ligne">
        {/* Le visage mene au profil : dans un groupe, c'est souvent la
            seule prise qu'on ait sur quelqu'un qu'on ne connait pas. La
            gouttiere reste posee meme vide, pour tenir la place. */}
        {visage ? (
          <Link
            className="ms-msg__visage"
            to={`/app/members/${visage.id}`}
            aria-label={t.messages.conversation.voirProfil(visage.username)}
          >
            <Avatar user={visage} size={26} />
          </Link>
        ) : (
          <span className="ms-msg__visage" />
        )}
        {contenu}
      </div>
    ) : (
      contenu
    );

  /*
    Un message retire laisse sa place dans le fil, videe de son contenu :
    sans cette trace, le message s'evaporait chez l'autre et il ne restait
    qu'un blanc entre deux repliques. Il garde sa bulle et son heure : une
    ligne grise sans fond se lisait comme une mention de l'application,
    pas comme la replique de quelqu'un.
  */
  if (message.deleted) {
    return (
      <div className={`ms-msg${mine ? ' ms-msg--mine' : ''}${enGroupe ? ' ms-msg--signe' : ''}`}>
        {signature}
        {encadrer(
          <p className="ms-bubble ms-bubble--supprime">
            {/* Le signe des messageries : on reconnait un retrait avant de lire. */}
            <Prohibit className="ms-bubble__retire" size={16} aria-hidden="true" />
            {t.messages.commun.supprime}
            <Heure message={message} />
          </p>,
        )}
      </div>
    );
  }

  return (
    <div
      ref={glisse.ref as React.RefObject<HTMLDivElement>}
      {...glisse.handlers}
      data-message={message.id}
      className={`ms-msg ms-msg--glissable${mine ? ' ms-msg--mine' : ''}${
        enGroupe ? ' ms-msg--signe' : ''
      }${part ? ' ms-msg--part' : ''}`}
      onClick={surTouche}
    >
      {signature}
      {/*
        Ce a quoi ce message repond, rappele au-dessus de lui.

        Le texte d'aujourd'hui, pas une copie faite a l'envoi : un message
        corrige se lit corrige, et un message efface le dit.
      */}
      {message.replyTo && (
        <button
          type="button"
          className="ms-cite"
          /*
            Le rappel n'etait qu'un rappel : on lisait « en reponse a
            quoi » sans pouvoir aller voir. C'est pourtant la seule chose
            qu'on veut faire en le lisant.

            Elle ne touche pas au clavier. Refuser le focus par defaut
            evite qu'elle le prenne ; elle ne le rend pas non plus,
            puisqu'elle ne l'a pas pris. Le clavier ne se leve que de deux
            facons — toucher la saisie, ou tirer un message pour y
            repondre — et une regle aussi simple vaut mieux qu'un temoin
            qu'il faut tenir a jour.
          */
          onPointerDown={(evenement) => evenement.preventDefault()}
          onMouseDown={(evenement) => evenement.preventDefault()}
          onClick={() => cite && onAllerA(cite.id)}
          disabled={!cite}
          aria-label={cite ? t.messages.conversation.voirCite : undefined}
        >
          <span className="ms-cite__qui">{citeDe ?? t.messages.commun.message}</span>
          {cite?.voice ? (
            <VoiceApercu voice={cite.voice} />
          ) : (
            <span className="ms-cite__quoi">
              {cite ? apercuDeCite(cite) : t.messages.commun.supprime}
            </span>
          )}
        </button>
      )}
      {encadrer(
        message.call ? (
          /*
          La trace d'un appel : une ligne, pas une bulle. Ce n'est pas
          quelque chose que quelqu'un a ecrit, c'est quelque chose qui
          s'est passe — et le fil doit le donner a lire comme tel.
        */
          <span className={`ms-call${message.call.missed ? ' ms-call--missed' : ''}`} {...hold}>
            {/*
            Le signe dit le moyen, la couleur dit l'issue. Un combine et
            une camera ne se confondent pas d'un coup d'oeil, la ou deux
            lignes de texte demandent d'etre lues.
          */}
            {message.call.video ? (
              message.call.missed ? (
                <VideoCameraSlash size={18} />
              ) : (
                <CameraArrondie size={18} />
              )
            ) : message.call.missed ? (
              <PhoneX size={17} />
            ) : (
              <Phone size={17} />
            )}
            {/*
            « Manque » des deux cotes. On disait « sans reponse » a celui
            qui avait appele : plus exact, mais deux mots pour la meme
            chose obligeaient a se demander lequel on lisait.
          */}
            {message.call.missed
              ? t.messages.conversation.appel.manque(message.call.video)
              : t.messages.conversation.appel.duree(message.call.video, stopwatch(message.call.durationMs))}
            <Heure message={message} en="ligne" />
          </span>
        ) : message.attachedEventId ? (
          /*
            La prise descend jusqu'aux cartes.

            Elles etaient les seules bulles a ne pas repondre a l'appui
            long : un evenement recu ne se supprimait pas, ne se citait pas
            et ne recevait pas d'emoji, alors que c'est un message comme
            les autres. Elle va aussi au gabarit d'attente — une carte qui
            se charge est deja un message.
          */
          <CarteEvenement eventId={message.attachedEventId} prise={hold} />
        ) : message.attachedPostId ? (
          <CartePartagee postId={message.attachedPostId} prise={hold} />
        ) : message.voice ? (
          <span className="ms-voice" {...hold}>
            <VoicePlayer id={message.id} voice={message.voice} mine={mine} nextId={nextVoiceId} />
            <Heure message={message} en="ligne" />
          </span>
        ) : message.video ? (
          /*
          Le lecteur natif, avec ses propres commandes. `playsInline`
          empeche iOS de basculer en plein ecran des la lecture — une
          video de dix secondes se regarde dans le fil, pas ailleurs.
        */
          <span className="ms-video" {...hold}>
            {/*
              L'image de couverture porte l'affichage, pas la video.

              Le fil demandait l'en-tete de chaque film pour en tirer une
              premiere image : autant de requetes a l'ouverture, et un
              rectangle noir en attendant. Avec une couverture, il ne
              charge plus rien tant qu'on n'appuie pas. Les videos
              d'avant n'en ont pas — elles gardent l'ancien
              comportement, faute de mieux.
            */}
            <video
              src={message.video.videoUrl}
              poster={message.video.posterUrl ?? undefined}
              controls
              playsInline
              preload={message.video.posterUrl ? 'none' : 'metadata'}
            />
            {message.content ? (
              <span className="ms-photo__caption">
                {message.content}
                <Heure message={message} />
              </span>
            ) : (
              /* En haut : le bas du film appartient aux commandes du lecteur. */
              <Heure message={message} en="video" />
            )}
          </span>
        ) : message.imageUrl ? (
          <span
            className="ms-photo"
            /*
              Le cadre est connu avant que la photo arrive : c'est ce qui
              empeche le fil de s'allonger sous les yeux de qui le lit.
              Les photos d'avant n'ont pas leur rapport en base et gardent
              le cadre par defaut de la feuille de style.
            */
            style={
              message.imageRatio
                ? ({ ['--rapport' as string]: String(message.imageRatio) } as never)
                : undefined
            }
            {...hold}
          >
            <button
              className="ms-photo__open"
              aria-label={t.fil.commun.voirPhoto}
              /* La place de la vignette part avec l'adresse : la
                 visionneuse en a besoin pour savoir d'ou grandir. */
              onClick={(event) =>
                onOpenPhoto(message.imageUrl!, event.currentTarget.getBoundingClientRect())
              }
            >
              <img src={message.imageUrl} alt={t.messages.conversation.photoEnvoyee} />
            </button>
            {message.content ? (
              <span className="ms-photo__caption">
                {message.content}
                <Heure message={message} />
              </span>
            ) : (
              <Heure message={message} en="photo" />
            )}
          </span>
        ) : (
          <p className="ms-bubble" {...hold}>
            {message.content}
            <Heure message={message} />
          </p>
        ),
      )}

      {/*
        Les emojis poses, sous la bulle et du cote de son auteur.

        Regroupes par emoji avec leur compte : dix pouces font une
        pastille et un « 10 », pas dix pastilles. Ils ne sont pas
        cliquables — la feuille est le seul endroit ou l'on agit, et
        rester appuye pour retirer ce qu'on a pose est le meme geste que
        pour le poser.
      */}
      {(compte.length > 0 || sortantes.length > 0) && (
        <span className="ms-react">
          {compte.map(([emoji, n]) => (
            <button
              key={emoji}
              type="button"
              className={`ms-react__pastille${
                neuves.includes(emoji) ? ' ms-react__pastille--neuve' : ''
              }`}
              aria-label={t.messages.conversation.voirReactions}
              onClick={() => onVoirReactions(message)}
            >
              {emoji}
              {n > 1 && <span className="ms-react__n">{n}</span>}
            </button>
          ))}
          {/* Retirees, mais gardees le temps de s'en aller. */}
          {sortantes.map((emoji) => (
            <span key={`part-${emoji}`} className="ms-react__pastille ms-react__pastille--part">
              {emoji}
            </span>
          ))}
        </span>
      )}

      {/*
        Sous la bulle, ce qui n'a pas sa place dedans : l'heure d'une carte
        partagee, qui est la vraie carte et non une bulle, et « Lu », au
        pied de la salve seulement.
      */}
      {(carte || lu) && (
        <span className="ms-msg__time">
          {carte && texteDeLHeure(message)}
          {carte && lu && ' · '}
          {lu && t.messages.conversation.lu}
        </span>
      )}
    </div>
  );
}

/** « 12:40 », ou « Modifié 12:40 » : l'autre a pu lire la premiere version. */
function texteDeLHeure(message: Message): string {
  const heure = timeOfDay(message.createdAt);
  return message.modifie ? `${t.messages.conversation.modifie} ${heure}` : heure;
}

/**
 * L'heure de chaque message, en bas a droite de sa bulle.
 *
 * Elle ne se disait que sous le dernier message d'une salve : savoir
 * quand une replique du milieu avait ete ecrite demandait de remonter
 * jusqu'a la suivante. Dans le texte, une copie invisible lui tient la
 * place au bout de la derniere ligne : si la ligne a de la place, l'heure
 * s'y pose ; sinon la bulle en prend une de plus, comme dans les
 * messageries. Sur une photo ou une video sans legende, elle se pose sur
 * l'image ; a cote d'un vocal ou d'un appel, elle suit sur la ligne.
 */
function Heure({ message, en }: { message: Message; en?: 'photo' | 'video' | 'ligne' }) {
  const texte = texteDeLHeure(message);
  if (en === 'ligne') return <span className="ms-heure-ligne">{texte}</span>;
  if (en) return <span className={`ms-heure ms-heure--${en}`}>{texte}</span>;
  return (
    <>
      <span className="ms-heure__place" aria-hidden="true">
        {texte}
      </span>
      <span className="ms-heure">{texte}</span>
    </>
  );
}

/**
 * Evenement partage dans la discussion.
 * La carte remplace la bulle : on lit la sortie, pas un lien nu.
 *
 * Meme cle de cache que dans le feed, et pour la meme raison : la fiche
 * ne doit pas se relire a vide chaque fois qu'on rouvre le fil.
 */
function CarteEvenement({ eventId, prise }: { eventId: string; prise: PriseLongue }) {
  const { data } = useAsync(() => events.get(eventId), [eventId], `event:${eventId}`);
  useEffect(() => {
    if (data) cartePrete();
  }, [data]);

  /* La place est tenue pendant la lecture. Rendre `null` faisait une
     bulle de hauteur nulle qui prenait d'un coup sa taille pleine : tout
     ce qui la suivait descendait, et le fil semblait avoir remonte. */
  if (!data) return <div className="card ms-event ms-event--attente" {...prise} />;

  /*
    La carte de l'onglet Evenements, en petit.

    Le fil n'en montrait qu'un resume maison — un titre, une ligne de
    faits — la ou la meme sortie a partout ailleurs une affiche, sa
    categorie, et le fait qu'on y est inscrit. Recevoir une sortie
    donnait donc moins a voir que la croiser, alors que c'est le moment
    ou l'on decide d'y aller.

    C'est la vraie carte, pas une copie : ce qu'on lui ajoutera se lira
    ici aussi, et il n'y a plus deux visages pour une meme sortie.
  */
  return (
    <div className="ms-event" {...prise}>
      <EventCard event={data} compact />
    </div>
  );
}

/**
 * Post partage dans la discussion.
 *
 * La carte remplace la bulle, comme pour un evenement : on lit le post,
 * pas un lien nu. C'est l'identifiant qui voyage, donc le texte affiche
 * est celui d'aujourd'hui — un post modifie depuis se lit modifie, un
 * post efface ne laisse pas de copie.
 */
function CartePartagee({ postId, prise }: { postId: string; prise: PriseLongue }) {
  const aller = useNavigate();
  const { data, loading } = useAsync(() => posts.byId(postId), [postId], `post:${postId}`);
  useEffect(() => {
    if (!loading) cartePrete();
  }, [loading]);

  /*
    Une carte vide tant qu'on lit, mais qui tient sa place.

    Une mention d'indisponibilite se dementirait une seconde plus tard,
    et `null` ne valait pas mieux : la bulle passait de rien a cent
    quarante pixels, poussait tout ce qui la suit vers le bas, et le fil
    paraissait avoir remonte juste apres son ouverture.
  */
  if (loading) return <div className="card ms-post ms-post--attente" {...prise} />;
  if (!data) {
    return (
      <div className="card ms-post" {...prise}>
        <p className="ms-post__absent">{t.messages.conversation.postIndisponible}</p>
      </div>
    );
  }

  /*
    Le post tel qu'il se lit dans le feed, en petit.

    Le fil n'en montrait qu'un resume maison — un nom, trois lignes de
    texte — la ou le meme post a partout ailleurs son visage, son age, et
    ce que les autres en ont fait. Recevoir un post donnait moins a voir
    que le croiser.

    C'est la vraie carte, pas une copie : ce qu'on lui ajoutera se lira
    ici aussi. Elle vient sans ses gestes — on ouvre le post pour agir
    dessus, et le message a deja les siens par l'appui long.
  */
  return (
    <div className="card ms-post" {...prise}>
      <CartePost
        post={data}
        compact
        /* Le post part avec la navigation : l'ecran d'arrivee l'a deja
           entier, et le relire couterait trois allers-retours en chaine
           pour afficher ce qu'on avait sous les yeux. */
        onOpenComments={(post) => aller(`/app/feed/${post.id}/comments`, { state: { post } })}
      />
    </div>
  );
}

/*
  Les posts et evenements cites dans le fil, lus avec lui.

  Une carte partagee allait chercher son contenu toute seule, une fois le
  fil affiche : on voyait un rectangle gris devenir du texte. On repond
  d'avance a la question qu'elle va poser, en posant la reponse dans le
  cache sous la cle qu'elle utilisera — elle s'affiche alors pleine des
  le premier rendu.

  Les lectures partent ensemble, et sous une limite de temps. Un post
  efface, une lecture qui traine : rien de tout cela ne doit retenir une
  conversation. Passe le delai on affiche le fil, et la carte ira se
  chercher elle-meme, comme avant.
*/
const ATTENTE_CARTES_MS = 1200;

async function prechargerLesCartes(fil: Message[]): Promise<void> {
  const postIds = [...new Set(fil.map((m) => m.attachedPostId).filter(Boolean))] as string[];
  const eventIds = [...new Set(fil.map((m) => m.attachedEventId).filter(Boolean))] as string[];
  if (postIds.length === 0 && eventIds.length === 0) return;

  const lectures = [
    ...postIds.map((id) =>
      posts
        .byId(id)
        .then((post) => seedScreenCache(`post:${id}`, post))
        .catch(() => undefined),
    ),
    ...eventIds.map((id) =>
      events
        .get(id)
        .then((evenement) => seedScreenCache(`event:${id}`, evenement))
        .catch(() => undefined),
    ),
  ];

  await Promise.race([
    Promise.all(lectures),
    new Promise((suite) => window.setTimeout(suite, ATTENTE_CARTES_MS)),
  ]);
}

/*
  Les cartes qui se remplissent apres coup previennent le fil.

  Elles sont trois niveaux sous lui, et leur passer une fonction de main
  en main aurait alourdi trois signatures pour un signal qui ne dit rien
  d'autre que « quelque chose vient de changer de hauteur ». Un carnet
  d'abonnes local au fichier fait le meme travail sans traverser
  l'arbre.
*/
const abonnesAuxCartes = new Set<() => void>();

function surCartePrete(prevenir: () => void): () => void {
  abonnesAuxCartes.add(prevenir);
  return () => {
    abonnesAuxCartes.delete(prevenir);
  };
}

function cartePrete(): void {
  abonnesAuxCartes.forEach((prevenir) => prevenir());
}

/** Deux messages tombent-ils le meme jour. */
function isSameDay(left: string, right: string): boolean {
  return new Date(left).toDateString() === new Date(right).toDateString();
}

/** Millisecondes -> "0:07", pour le compteur d'enregistrement. */
function stopwatch(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
